import React, { useState, useEffect } from 'react';

// ═══════════════════════════════════════════════════════
// AVENIR TECH WEEK · May 27, 2026 · MIT iHQ
// Half-day invite-only gathering — AI strategy × benefits
// ═══════════════════════════════════════════════════════

export default function AvenirTechWeek() {
    const [activeTab, setActiveTab] = useState('about');
    const [rsvpState, setRsvpState] = useState(null);
    const [openFaq, setOpenFaq] = useState(0);
    const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0 });
    const [toast, setToast] = useState(null);

    // Countdown to May 27, 2026 1:00 PM ET (half-day afternoon event)
    useEffect(() => {
        const target = new Date('2026-05-27T13:00:00-04:00').getTime();
        const tick = () => {
            const diff = target - Date.now();
            if (diff <= 0) return setCountdown({ d: 0, h: 0, m: 0, s: 0 });
            setCountdown({
                d: Math.floor(diff / 86400000),
                h: Math.floor((diff % 86400000) / 3600000),
                m: Math.floor((diff % 3600000) / 60000),
                s: Math.floor((diff % 60000) / 1000),
            });
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(null), 2800);
    };

    // Calendar action — generate .ics download
    const handleAddToCalendar = () => {
        const ics = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Avenir AI//Tech Week 2026//EN',
            'BEGIN:VEVENT',
            'UID:avenir-tech-week-2026@avenirai.com',
            'DTSTAMP:20260401T000000Z',
            'DTSTART:20260527T170000Z', // 1pm ET = 17:00 UTC
            'DTEND:20260527T220000Z',   // 6pm ET
            'SUMMARY:Avenir Tech Week 2026',
            'DESCRIPTION:A half-day invite-only gathering on AI strategy for the employee benefits industry. Hosted at MIT iHQ.',
            'LOCATION:MIT iHQ\\, 314 Main St\\, Cambridge\\, MA 02142',
            'END:VEVENT',
            'END:VCALENDAR',
        ].join('\r\n');
        const blob = new Blob([ics], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'avenir-tech-week-2026.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('Calendar invite downloaded');
    };

    // Share action — Web Share API or clipboard fallback
    const handleShare = async () => {
        const shareData = {
            title: 'Avenir Tech Week 2026',
            text: 'Half-day invite-only gathering on AI strategy for the benefits industry. May 27 at MIT iHQ.',
            url: typeof window !== 'undefined' ? window.location.href : 'https://avenirai.com/tech-week',
        };
        try {
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share(shareData);
            } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(shareData.url);
                showToast('Link copied to clipboard');
            } else {
                showToast('Share not supported on this device');
            }
        } catch (err) {
            if (err?.name !== 'AbortError') showToast('Share cancelled');
        }
    };

    const tabs = [
        { id: 'about', label: 'Overview', num: '01' },
        { id: 'speakers', label: 'Speakers', num: '02' },
        { id: 'agenda', label: 'Agenda', num: '03' },
        { id: 'sponsors', label: 'Sponsors', num: '04' },
        { id: 'faq', label: 'FAQ', num: '05' },
    ];

    return (
        <div className="atw-root">
            <AtwStyles />
            <AmbientBackdrop />

            <main className="atw-main">
                <Hero
                    countdown={countdown}
                    rsvpState={rsvpState}
                    setRsvpState={setRsvpState}
                    onAddToCalendar={handleAddToCalendar}
                    onShare={handleShare}
                />
                <Marquee />

                <section className="atw-tabs-section" id="program">
                    <div className="atw-tabs-header">
                        <div>
                            <div className="atw-eyebrow">◆ The Afternoon</div>
                            <h2 className="atw-section-title">
                                One half-day at MIT.<br />
                                <em>Five conversations</em> that matter.
                            </h2>
                        </div>
                        <p className="atw-section-sub">
                            No trade-show booths. No vendor pitches. Just genuine conversations among leaders.
                            Tap through the program below.
                        </p>
                    </div>

                    <nav className="atw-tablist" role="tablist">
                        {tabs.map(t => (
                            <button
                                key={t.id}
                                role="tab"
                                aria-selected={activeTab === t.id}
                                className={`atw-tab ${activeTab === t.id ? 'is-active' : ''}`}
                                onClick={() => setActiveTab(t.id)}
                            >
                                <span className="atw-tab-num">{t.num}</span>
                                <span className="atw-tab-label">{t.label}</span>
                            </button>
                        ))}
                    </nav>

                    <div className="atw-panels">
                        {activeTab === 'about' && <AboutPanel />}
                        {activeTab === 'speakers' && <SpeakersPanel />}
                        {activeTab === 'agenda' && <AgendaPanel />}
                        {activeTab === 'sponsors' && <SponsorsPanel />}
                        {activeTab === 'faq' && <FaqPanel openFaq={openFaq} setOpenFaq={setOpenFaq} />}
                    </div>
                </section>

                <FinalCta />
            </main>

            {toast && (
                <div className="atw-toast" role="status" aria-live="polite">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {toast}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// AMBIENT BACKDROP
// ═══════════════════════════════════════════════════════
function AmbientBackdrop() {
    return (
        <div className="atw-backdrop" aria-hidden="true">
            <div className="atw-glow atw-glow-1" />
            <div className="atw-glow atw-glow-2" />
            <div className="atw-glow atw-glow-3" />
            <div className="atw-vignette" />
            <Particles />
        </div>
    );
}

function Particles() {
    const particles = Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 2 + Math.random() * 3,
        duration: 10 + Math.random() * 12,
        delay: Math.random() * 10,
    }));
    return (
        <div className="atw-particles">
            {particles.map(p => (
                <span
                    key={p.id}
                    className="atw-particle"
                    style={{
                        left: `${p.left}%`,
                        top: `${p.top}%`,
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${p.delay}s`,
                    }}
                />
            ))}
        </div>
    );
}



// ═══════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════
function Hero({ countdown, rsvpState, setRsvpState, onAddToCalendar, onShare }) {
    return (
        <section className="atw-hero">
            <div className="atw-hero-inner">
                {/* LEFT */}
                <div className="atw-hero-left">
                    <div className="atw-hero-badge">
                        <span className="atw-hero-badge-label">Presented by</span>
                        <span className="atw-hero-badge-divider" />
                        <img
                            src="/techweek.jpg"
                            alt="Tech Week"
                            className="atw-hero-badge-logo atw-hero-badge-logo-tw"
                        /> Tech Week
                        <img
                            src="/avenirlogowhite.png"
                            alt="Avenir"
                            className="atw-hero-badge-logo atw-hero-badge-logo-av"
                        /> Avenir AI
                    </div>
                    <br /><br />
                    <h1 className="atw-hero-title">
                        AI × Employee<br />
                        Benefits:<br />
                        <em>Finding Cost<br />Drivers</em>
                    </h1>

                    <div className="atw-hero-date-block">
                        <div className="atw-hero-date-main">Wednesday, May 27 · 2026</div>
                        <div className="atw-hero-date-sub">1:00 PM → 6:00 PM ET</div>
                    </div>

                    <div className="atw-hero-actions">
                        <button
                            className="atw-circle-btn"
                            aria-label="Add to calendar"
                            onClick={onAddToCalendar}
                            title="Download .ics"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                <rect x="3" y="5" width="18" height="16" rx="2" />
                                <path d="M3 9h18M8 3v4M16 3v4" strokeLinecap="round" />
                            </svg>
                        </button>
                        <button
                            className="atw-circle-btn"
                            aria-label="Share"
                            onClick={onShare}
                            title="Copy link"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                <circle cx="18" cy="5" r="3" />
                                <circle cx="6" cy="12" r="3" />
                                <circle cx="18" cy="19" r="3" />
                                <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
                            </svg>
                        </button>
                    </div>

                    <div className="atw-hero-host-row">
                        <div className="atw-hero-host-label">Hosted by</div>
                        <div className="atw-hero-hosts">
                            <HostAvatar
                                name="Maria Zou"
                                role="Co-Founder & CEO, Avenir AI"
                                initials="MZ"
                                gradient="radial-gradient(at 30% 30%, #FDA4AF, #BE123C 70%), linear-gradient(135deg, #7C2D12, #450A0A)"
                            />
                            <HostAvatar
                                name="Ari Dulchinos"
                                role="Co-Founder & CTO, Avenir AI"
                                initials="AD"
                                gradient="radial-gradient(at 40% 30%, #FCD34D, #B45309 70%), linear-gradient(135deg, #78350F, #451A03)"
                            />
                            <HostAvatar
                                name="Tech Week"
                                role="Partner · Boston 2026"
                                image="/techweek.jpg"
                            />
                        </div>
                    </div>

                    <div className="atw-hero-info-list">
                        <div className="atw-hero-info">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                            <div>
                                <div className="atw-info-primary">MIT iHQ</div>
                                <div className="atw-info-secondary">314 Main St · Cambridge, MA 02142</div>
                            </div>
                        </div>
                        <div className="atw-hero-info">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
                                <path d="M20 7L9 18l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <div className="atw-info-primary">Complimentary · Invite only</div>
                                <div className="atw-info-secondary">Seats extended to a curated list of ~50 leaders</div>
                            </div>
                        </div>
                        <div className="atw-hero-info">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 2" strokeLinecap="round" />
                            </svg>
                            <div>
                                <div className="atw-info-primary">Half-day · 5 hours</div>
                                <div className="atw-info-secondary">Afternoon program + executive dinner (invite-only)</div>
                            </div>
                        </div>
                    </div>

                    <p className="atw-hero-desc">
                        Every year, HR leaders, CFOs, and benefits consultants spend hundreds of hours
                        manually sifting through claims, pharmacy, and utilization data to identify
                        what's actually driving plan costs — and the critical insights still slip through.
                    </p>
                    <p className="atw-hero-desc">
                        <em>Avenir Tech Week</em> is a half-day strategic forum on how AI is reshaping that work.
                        No code, no jargon. Built for the people <em>making the decisions</em> — brokers,
                        benefits leaders, CFOs, and the carriers and consultants working alongside them.
                    </p>
                </div>

                {/* RIGHT */}
                <div className="atw-hero-right">
                    <HeroArtwork />



                    <div className="atw-countdown">
                        <div className="atw-countdown-label">◆ DOORS OPEN IN</div>
                        <div className="atw-countdown-grid">
                            <CountUnit val={countdown.d} label="DAYS" />
                            <CountUnit val={countdown.h} label="HRS" />
                            <CountUnit val={countdown.m} label="MIN" />
                            <CountUnit val={countdown.s} label="SEC" />
                        </div>
                    </div>

                    <LiveAttendeeStrip />
                </div>
            </div>
        </section>
    );
}

function HostAvatar({ name, role, initials, gradient, image }) {
    return (
        <div className="atw-host">
            <div
                className="atw-host-avatar"
                style={{ background: image ? '#000' : gradient }}
            >
                {image ? (
                    <img src={image} alt={name} className="atw-host-avatar-img" />
                ) : (
                    <span className="atw-host-initials">{initials}</span>
                )}
            </div>
            <div className="atw-host-info">
                <div className="atw-host-name">{name}</div>
                <div className="atw-host-role">{role}</div>
            </div>
        </div>
    );
}

function CountUnit({ val, label }) {
    return (
        <div className="atw-count-unit">
            <div className="atw-count-val">{String(val).padStart(2, '0')}</div>
            <div className="atw-count-lbl">{label}</div>
        </div>
    );
}

function LiveAttendeeStrip() {
    const attendees = [
        { i: 'SK', g: 'linear-gradient(135deg,#BE123C,#7C2D12)' },
        { i: 'JR', g: 'linear-gradient(135deg,#F59E0B,#92400E)' },
        { i: 'DC', g: 'linear-gradient(135deg,#EC4899,#831843)' },
        { i: 'MP', g: 'linear-gradient(135deg,#8B5CF6,#4C1D95)' },
        { i: 'TH', g: 'linear-gradient(135deg,#06B6D4,#164E63)' },
    ];
    return (
        <div className="atw-live-strip">
            <div className="atw-live-avatars">
                {attendees.map((a, i) => (
                    <div key={i} className="atw-live-avatar" style={{ background: a.g }}>
                        {a.i}
                    </div>
                ))}
                <div className="atw-live-more">+33</div>
            </div>
            <div className="atw-live-text">
                <strong>38 of 50</strong> seats confirmed · waitlist opens at capacity
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// HERO ARTWORK — unique AI-meets-benefits generative piece
// A flow of claims data being filtered through neural
// nodes and rendered into actionable insight. Bespoke,
// animated SVG — not a generic "AI" mark.
// ═══════════════════════════════════════════════════════
function HeroArtwork() {
    return (
        <div className="atw-art">
            <div className="atw-art-frame">
                <svg viewBox="0 0 500 500" className="atw-art-svg" preserveAspectRatio="xMidYMid meet">
                    <defs>
                        {/* Background gradient — warm interior */}
                        <radialGradient id="atw-bg-grad" cx="50%" cy="45%" r="70%">
                            <stop offset="0%" stopColor="#2D0A14" />
                            <stop offset="60%" stopColor="#1A0810" />
                            <stop offset="100%" stopColor="#0D0409" />
                        </radialGradient>

                        {/* Glowing lines */}
                        <linearGradient id="atw-line-grad" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#F472B6" stopOpacity="0" />
                            <stop offset="50%" stopColor="#F472B6" stopOpacity="1" />
                            <stop offset="100%" stopColor="#FCD34D" stopOpacity="0" />
                        </linearGradient>

                        {/* Node glow */}
                        <radialGradient id="atw-node-glow" cx="50%" cy="50%">
                            <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.9" />
                            <stop offset="50%" stopColor="#F472B6" stopOpacity="0.4" />
                            <stop offset="100%" stopColor="#F472B6" stopOpacity="0" />
                        </radialGradient>

                        {/* Chart line gradient */}
                        <linearGradient id="atw-chart-grad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.4" />
                            <stop offset="100%" stopColor="#FCD34D" stopOpacity="0" />
                        </linearGradient>

                        {/* Filter that gives the neural network a glow */}
                        <filter id="atw-soft-glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2.5" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>

                        <filter id="atw-strong-glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="5" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Background */}
                    <rect width="500" height="500" fill="url(#atw-bg-grad)" />

                    {/* ═══ LEFT: Raw data stream (messy claims data) ═══ */}
                    <g className="atw-art-stream">
                        {/* Column of data "rows" flowing in */}
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <g key={`data-${i}`} transform={`translate(40, ${80 + i * 38})`}>
                                <rect
                                    x="0" y="0"
                                    width={44 + (i % 3) * 12}
                                    height="5"
                                    rx="1"
                                    fill="#6B1F3A"
                                    opacity={0.4 + (i % 3) * 0.15}
                                    className="atw-art-row"
                                    style={{ animationDelay: `${i * 0.15}s` }}
                                />
                                <rect
                                    x="0" y="9"
                                    width={28 + (i % 2) * 20}
                                    height="3"
                                    rx="1"
                                    fill="#F472B6"
                                    opacity={0.5}
                                    className="atw-art-row"
                                    style={{ animationDelay: `${i * 0.15 + 0.1}s` }}
                                />
                                <text x="0" y="22" fill="#6B1F3A" fontSize="6" fontFamily="JetBrains Mono, monospace" opacity="0.7">
                                    {['CPT-99213', 'DX-E11.9', 'NDC-00093', 'ICD-J45.909', 'CPT-85025', 'DX-I10', 'NDC-54092', 'CPT-80053'][i]}
                                </text>
                            </g>
                        ))}

                        {/* Label for raw data */}
                        <text x="40" y="68" fill="#F472B6" fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.5" opacity="0.8">
                            RAW CLAIMS
                        </text>
                    </g>

                    {/* ═══ CENTER: Neural processing nodes ═══ */}
                    <g className="atw-art-neural" filter="url(#atw-soft-glow)">
                        {/* Connection lines from data to layer 1 */}
                        {[0, 1, 2, 3].map((layer1Idx) => (
                            <g key={`l1-${layer1Idx}`}>
                                {[1, 3, 5, 7].map((dataIdx) => (
                                    <path
                                        key={`conn-${layer1Idx}-${dataIdx}`}
                                        d={`M 155 ${95 + dataIdx * 38} Q 200 ${180 + layer1Idx * 40}, 230 ${160 + layer1Idx * 45}`}
                                        stroke="#F472B6"
                                        strokeWidth="0.6"
                                        fill="none"
                                        opacity="0.25"
                                        className="atw-art-conn"
                                        style={{ animationDelay: `${(layer1Idx * 4 + dataIdx) * 0.08}s` }}
                                    />
                                ))}
                            </g>
                        ))}

                        {/* Layer 1 nodes */}
                        {[0, 1, 2, 3].map((i) => (
                            <g key={`n1-${i}`} transform={`translate(240, ${160 + i * 45})`}>
                                <circle r="14" fill="url(#atw-node-glow)" opacity="0.4" />
                                <circle r="7" fill="#2D0A14" stroke="#F472B6" strokeWidth="1.5" />
                                <circle r="3" fill="#F472B6" className="atw-art-node-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                            </g>
                        ))}

                        {/* Connections between layer 1 and layer 2 */}
                        {[0, 1, 2].map((layer2Idx) => (
                            <g key={`l2-${layer2Idx}`}>
                                {[0, 1, 2, 3].map((layer1Idx) => (
                                    <path
                                        key={`conn2-${layer2Idx}-${layer1Idx}`}
                                        d={`M 247 ${160 + layer1Idx * 45} Q 295 ${200 + layer2Idx * 50}, 330 ${185 + layer2Idx * 55}`}
                                        stroke="#FCD34D"
                                        strokeWidth="0.7"
                                        fill="none"
                                        opacity="0.3"
                                        className="atw-art-conn"
                                        style={{ animationDelay: `${(layer2Idx * 4 + layer1Idx) * 0.06 + 0.5}s` }}
                                    />
                                ))}
                            </g>
                        ))}

                        {/* Layer 2 nodes — bigger, more "thinking" */}
                        {[0, 1, 2].map((i) => (
                            <g key={`n2-${i}`} transform={`translate(340, ${185 + i * 55})`}>
                                <circle r="18" fill="url(#atw-node-glow)" opacity="0.5" />
                                <circle r="9" fill="#2D0A14" stroke="#FCD34D" strokeWidth="1.5" />
                                <circle r="4" fill="#FCD34D" className="atw-art-node-pulse" style={{ animationDelay: `${i * 0.4 + 0.3}s` }} />
                            </g>
                        ))}
                    </g>

                    {/* Label for processing */}
                    <text x="240" y="140" fill="#FCD34D" fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.5" opacity="0.8">
                        AVENIR AGENT
                    </text>

                    {/* ═══ RIGHT: Clean, actionable insight — a cost-driver dashboard ═══ */}
                    <g className="atw-art-output" transform="translate(380, 100)">
                        {/* Dashboard frame */}
                        <rect
                            x="0" y="0"
                            width="100" height="260"
                            rx="6"
                            fill="#1A0810"
                            stroke="#F472B6"
                            strokeWidth="0.8"
                            opacity="0.9"
                            filter="url(#atw-soft-glow)"
                        />

                        {/* Dashboard title bar */}
                        <rect x="4" y="4" width="92" height="14" rx="2" fill="#2D0A14" />
                        <circle cx="10" cy="11" r="1.2" fill="#FCD34D" />
                        <circle cx="14" cy="11" r="1.2" fill="#F472B6" opacity="0.5" />
                        <circle cx="18" cy="11" r="1.2" fill="#F472B6" opacity="0.3" />
                        <text x="25" y="13" fill="#F5E6D3" fontSize="4.5" fontFamily="JetBrains Mono, monospace">
                            cost_drivers.report
                        </text>

                        {/* Section: "Top Cost Drivers" label */}
                        <text x="8" y="28" fill="#FCD34D" fontSize="4" fontFamily="JetBrains Mono, monospace" letterSpacing="0.5">
                            TOP COST DRIVERS
                        </text>

                        {/* Bar chart — top cost drivers */}
                        <g className="atw-art-bars">
                            {[
                                { label: 'Specialty Rx', val: 85, color: '#F472B6' },
                                { label: 'Type 2 Diab.', val: 64, color: '#EC4899' },
                                { label: 'Cardiology', val: 47, color: '#BE123C' },
                                { label: 'MSK/Ortho', val: 38, color: '#9F1239' },
                                { label: 'Mental Hlth', val: 29, color: '#7C2D12' },
                            ].map((bar, i) => (
                                <g key={bar.label} transform={`translate(8, ${35 + i * 12})`}>
                                    <text x="0" y="4" fill="#F5E6D3" fontSize="3.5" fontFamily="Inter, sans-serif" opacity="0.85">
                                        {bar.label}
                                    </text>
                                    <rect x="0" y="6" width="84" height="3" rx="1" fill="#2D0A14" />
                                    <rect
                                        x="0" y="6"
                                        width={bar.val * 0.84}
                                        height="3"
                                        rx="1"
                                        fill={bar.color}
                                        className="atw-art-bar"
                                        style={{ animationDelay: `${1.2 + i * 0.12}s`, transformOrigin: 'left center' }}
                                    />
                                    <text x="86" y="9" fill={bar.color} fontSize="3" fontFamily="JetBrains Mono, monospace">
                                        {bar.val}%
                                    </text>
                                </g>
                            ))}
                        </g>

                        {/* Divider */}
                        <line x1="8" y1="105" x2="92" y2="105" stroke="#F472B6" strokeWidth="0.3" opacity="0.3" />

                        {/* Section: "Trend" label */}
                        <text x="8" y="115" fill="#FCD34D" fontSize="4" fontFamily="JetBrains Mono, monospace" letterSpacing="0.5">
                            PMPM TREND · 12 MOS
                        </text>

                        {/* Line chart area */}
                        <g transform="translate(8, 120)">
                            {/* Axis */}
                            <line x1="0" y1="45" x2="84" y2="45" stroke="#F472B6" strokeWidth="0.3" opacity="0.25" />
                            <line x1="0" y1="22.5" x2="84" y2="22.5" stroke="#F472B6" strokeWidth="0.2" opacity="0.15" strokeDasharray="1 1" />

                            {/* Filled area under line */}
                            <path
                                d="M 0 38 L 7 35 L 14 36 L 21 32 L 28 28 L 35 30 L 42 24 L 49 22 L 56 17 L 63 14 L 70 12 L 77 8 L 84 5 L 84 45 L 0 45 Z"
                                fill="url(#atw-chart-grad)"
                                className="atw-art-chart-area"
                            />

                            {/* Trend line */}
                            <path
                                d="M 0 38 L 7 35 L 14 36 L 21 32 L 28 28 L 35 30 L 42 24 L 49 22 L 56 17 L 63 14 L 70 12 L 77 8 L 84 5"
                                stroke="#FCD34D"
                                strokeWidth="1"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="atw-art-chart-line"
                            />

                            {/* Data dots */}
                            {[0, 7, 14, 21, 28, 35, 42, 49, 56, 63, 70, 77, 84].map((x, i) => {
                                const y = [38, 35, 36, 32, 28, 30, 24, 22, 17, 14, 12, 8, 5][i];
                                return <circle key={i} cx={x} cy={y} r="1" fill="#FCD34D" className="atw-art-chart-dot" style={{ animationDelay: `${2 + i * 0.04}s` }} />;
                            })}
                        </g>

                        {/* Divider */}
                        <line x1="8" y1="175" x2="92" y2="175" stroke="#F472B6" strokeWidth="0.3" opacity="0.3" />

                        {/* Section: Key insight callout */}
                        <text x="8" y="185" fill="#FCD34D" fontSize="4" fontFamily="JetBrains Mono, monospace" letterSpacing="0.5">
                            ACTIONABLE INSIGHT
                        </text>
                        <rect x="6" y="190" width="88" height="36" rx="3" fill="#2D0A14" stroke="#FCD34D" strokeWidth="0.4" opacity="0.9" />
                        <text x="10" y="199" fill="#F5E6D3" fontSize="3.8" fontFamily="Fraunces, serif" fontStyle="italic">
                            Specialty Rx up
                        </text>
                        <text x="10" y="205" fill="#F5E6D3" fontSize="3.8" fontFamily="Fraunces, serif" fontStyle="italic">
                            23% YoY — review
                        </text>
                        <text x="10" y="211" fill="#F5E6D3" fontSize="3.8" fontFamily="Fraunces, serif" fontStyle="italic">
                            PBM contract
                        </text>
                        <text x="10" y="217" fill="#F5E6D3" fontSize="3.8" fontFamily="Fraunces, serif" fontStyle="italic">
                            terms Q2.
                        </text>

                        {/* Small indicator dot */}
                        <circle cx="88" cy="198" r="1.5" fill="#FCD34D" className="atw-art-node-pulse" />

                        {/* Footer — "powered by" */}
                        <text x="50" y="248" textAnchor="middle" fill="#F472B6" fontSize="3" fontFamily="JetBrains Mono, monospace" opacity="0.5" letterSpacing="1">
                            AVENIR · LIVE
                        </text>
                    </g>

                    {/* Label above dashboard */}
                    <text x="380" y="90" fill="#F472B6" fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.5" opacity="0.8">
                        THE INSIGHT
                    </text>

                    {/* ═══ ANIMATED DATA PARTICLES FLOWING ALONG THE PIPELINE ═══ */}
                    <g className="atw-art-particles">
                        {[0, 1, 2, 3, 4].map(i => (
                            <circle
                                key={`flow-${i}`}
                                r="1.5"
                                fill="#FCD34D"
                                filter="url(#atw-strong-glow)"
                                opacity="0"
                            >
                                <animateMotion
                                    dur="4s"
                                    repeatCount="indefinite"
                                    begin={`${i * 0.8}s`}
                                    path="M 100 250 Q 200 200, 280 230 T 420 230"
                                />
                                <animate
                                    attributeName="opacity"
                                    values="0; 1; 1; 0"
                                    dur="4s"
                                    repeatCount="indefinite"
                                    begin={`${i * 0.8}s`}
                                />
                            </circle>
                        ))}
                    </g>

                    {/* Decorative corner marks — subtle blueprint feel */}
                    <g stroke="#F472B6" strokeWidth="0.5" fill="none" opacity="0.3">
                        <path d="M 10 10 L 10 20 M 10 10 L 20 10" />
                        <path d="M 490 10 L 490 20 M 490 10 L 480 10" />
                        <path d="M 10 490 L 10 480 M 10 490 L 20 490" />
                        <path d="M 490 490 L 490 480 M 490 490 L 480 490" />
                    </g>

                    {/* Top caption — frames the whole artwork narrative */}
                    <text x="250" y="30" textAnchor="middle" fill="#F5E6D3" fontSize="11" fontFamily="Fraunces, serif" fontStyle="italic" opacity="0.9">
                        From chaos to clarity, in real time.
                    </text>
                    <text x="250" y="44" textAnchor="middle" fill="#F472B6" fontSize="6" fontFamily="JetBrains Mono, monospace" letterSpacing="2" opacity="0.7">
                        A V E N I R  ·  P I P E L I N E
                    </text>

                    {/* Bottom caption — the "so what" */}
                    <text x="250" y="480" textAnchor="middle" fill="#F5E6D3" fontSize="6.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1" opacity="0.55">
                        1.2M CLAIMS  →  47 DRIVERS  →  ONE REPORT
                    </text>
                </svg>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// MARQUEE
// ═══════════════════════════════════════════════════════
function Marquee() {
    const items = [
        'Claims intelligence',
        'Specialty Rx strategy',
        'PBM renegotiation',
        'Population risk foresight',
        'Carrier-side AI',
        'Stewardship automation',
        'Broker deliverables',
        'The self-insured thesis',
    ];
    const doubled = [...items, ...items];
    return (
        <div className="atw-marquee">
            <div className="atw-marquee-track">
                {doubled.map((t, i) => (
                    <span key={i} className="atw-marquee-item">
                        <span className="atw-marquee-star">✦</span>
                        <span>{t}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// ABOUT PANEL
// ═══════════════════════════════════════════════════════
function AboutPanel() {
    return (
        <div className="atw-panel">
            <div className="atw-about-hero">
                <div className="atw-about-lead">
                    <div className="atw-about-quote-mark">"</div>
                    <p>
                        The last ten years of employee benefits were spent digitizing paper.
                        The next ten will be spent <em>automating judgment</em> — and the people in this room
                        are the ones deciding how.
                    </p>
                    <div className="atw-about-attrib">
                        — Maria Zou, from the opening remarks
                    </div>
                </div>

            </div>

            <div className="atw-about-divider" />

            {/* Pillars — repositioned around strategic themes, not technical */}
            <div className="atw-pillars">
                <PillarCard
                    num="01 / VISION"
                    accent="var(--atw-rose)"
                    title="Where benefits is headed"
                    desc="The forces reshaping employer health strategy — and the AI capabilities that turn reactive plan management into a forward-looking competitive advantage."
                    stats={[
                        { k: '2026', v: 'and beyond' },
                        { k: '—', v: 'first-look product reveals' },
                        { k: '—', v: 'no slides, live demos' },
                    ]}
                />
                <PillarCard
                    num="02 / BUILD"
                    accent="var(--atw-gold)"
                    title="From insight to action"
                    desc="Watch a raw claims file become a board-ready strategy in real time. See the tools that are collapsing weeks of analyst work into minutes."
                    stats={[
                        { k: '1', v: 'live build, start to finish' },
                        { k: '—', v: 'real data, real output' },
                        { k: '0', v: 'slideware' },
                    ]}
                />
                <PillarCard
                    num="03 / CONNECT"
                    accent="var(--atw-teal)"
                    title="The room is the product"
                    desc="Brokers, benefits leaders, and builders — in one room, by invitation. The conversations that don't happen on Zoom."
                    stats={[
                        { k: '~50', v: 'leaders, invite-only' },
                        { k: '—', v: 'curated roundtables' },
                        { k: '1', v: 'executive dinner' },
                    ]}
                />
            </div>

            {/* Attendee breakdown */}
            <AttendeeBreakdown />
        </div>
    );
}


function PillarCard({ num, accent, title, desc, stats }) {
    return (
        <div className="atw-pillar" style={{ '--atw-pillar-accent': accent }}>
            <div className="atw-pillar-top">
                <div className="atw-pillar-num">{num}</div>
                <h4 className="atw-pillar-title">{title}</h4>
                <p className="atw-pillar-desc">{desc}</p>
            </div>
            <div className="atw-pillar-stats">
                {stats.map(s => (
                    <div key={s.v} className="atw-pillar-stat">
                        <div className="atw-pillar-stat-k">{s.k}</div>
                        <div className="atw-pillar-stat-v">{s.v}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AttendeeBreakdown() {
    const communities = [
        {
            label: 'Benefits Thought Leaders',
            sublabel: '',
            color: '#BE123C',
            glow: 'rgba(190, 18, 60, 0.4)',
            count: 8,
            radius: 110,
            duration: 40,
            size: 14,
            startAngle: 0,
        },
        {
            label: 'Frontier AI Technologists',
            sublabel: '',
            color: '#F59E0B',
            glow: 'rgba(245, 158, 11, 0.4)',
            count: 6,
            radius: 175,
            duration: 55,
            size: 12,
            startAngle: 30,
        },
        {
            label: 'Researchers',
            sublabel: '',
            color: '#06B6D4',
            glow: 'rgba(6, 182, 212, 0.4)',
            count: 5,
            radius: 235,
            duration: 75,
            size: 11,
            startAngle: 60,
        },
    ];

    const [hovered, setHovered] = React.useState(null);

    return (
        <div className="atw-breakdown">
            <div className="atw-breakdown-header">
                <div className="atw-eyebrow atw-eyebrow-warm">◆ WHO'S IN THE ROOM</div>
                <h3 className="atw-subsection-title">
                    Three communities, <em>one conversation</em>.<br />
                    By design.
                </h3>
            </div>

            <div className="atw-orbit-stage">
                <div className="atw-orbit-container">
                    {/* Orbit rings */}
                    {communities.map((c, i) => (
                        <div
                            key={`ring-${c.label}`}
                            className="atw-orbit-ring"
                            style={{
                                width: c.radius * 2,
                                height: c.radius * 2,
                                borderColor: hovered === i ? c.color : 'rgba(255,255,255,0.08)',
                                boxShadow: hovered === i ? `0 0 40px ${c.glow}` : 'none',
                            }}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(null)}
                        />
                    ))}

                    {/* Central sun */}
                    <div className="atw-orbit-sun">
                        <div className="atw-orbit-sun-core">
                            <div className="atw-orbit-sun-label">one</div>
                            <div className="atw-orbit-sun-label-big">conversation</div>
                        </div>
                    </div>

                    {/* Orbiting nodes — no handler on the group */}
                    {communities.map((c, i) => (
                        <div
                            key={`orbit-${c.label}`}
                            className="atw-orbit-group"
                            style={{
                                width: c.radius * 2,
                                height: c.radius * 2,
                                animationDuration: `${c.duration}s`,
                                animationPlayState: hovered === i ? 'paused' : 'running',
                                opacity: hovered !== null && hovered !== i ? 0.25 : 1,
                            }}
                        >
                            {Array.from({ length: c.count }).map((_, j) => {
                                const angle = (360 / c.count) * j + c.startAngle;
                                return (
                                    <div
                                        key={j}
                                        className="atw-orbit-node-wrap"
                                        style={{ transform: `rotate(${angle}deg) translateX(${c.radius}px)` }}
                                    >
                                        <div
                                            className="atw-orbit-node"
                                            style={{
                                                width: c.size,
                                                height: c.size,
                                                background: c.color,
                                                boxShadow: `0 0 12px ${c.glow}, 0 0 24px ${c.glow}`,
                                                animationDuration: `${c.duration}s`,
                                            }}
                                            onMouseEnter={() => setHovered(i)}
                                            onMouseLeave={() => setHovered(null)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Legend */}
                <div className="atw-orbit-legend">
                    {communities.map((c, i) => (
                        <div
                            key={c.label}
                            className={`atw-orbit-legend-item ${hovered === i ? 'is-active' : ''}`}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(null)}
                        >
                            <div className="atw-orbit-legend-dot" style={{ background: c.color, boxShadow: `0 0 16px ${c.glow}` }} />
                            <div>
                                <div className="atw-orbit-legend-label">{c.label}</div>
                                <div className="atw-orbit-legend-sub">{c.sublabel}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
// ═══════════════════════════════════════════════════════
// SPEAKERS PANEL
// ═══════════════════════════════════════════════════════
function SpeakersPanel() {
    const speakers = [
        {
            name: 'Maria Zou',
            role: 'Co-Founder & CEO',
            org: 'Avenir AI',
            slot: 'Opening · 1:05 PM',
            talk: 'The Decade of Benefits Automation',
            summary: 'Opening remarks — a strategic field map of where AI is actually working in benefits, and the three shifts that will reshape brokerage revenue models by 2030.',
            tag: 'OPENING',
            tagColor: '#BE123C',
            bio: 'MIT healthcare economics researcher. Clinical research at Harvard Chan School of Public Health. Ex-finance.',
            img: 'radial-gradient(at 30% 20%, #FECACA, #BE185D 60%), linear-gradient(135deg, #831843, #450A0A)',
        },
        {
            name: 'Ari Dulchinos',
            role: 'Co-Founder & CTO',
            org: 'Avenir AI',
            slot: 'Live demo · 3:15 PM',
            talk: 'Avenir, End-to-End',
            summary: 'A 20-minute live walkthrough of the Avenir platform — from a broker uploading a client\'s claims file to a board-ready stewardship deck. No slides, the product.',
            tag: 'DEMO',
            tagColor: '#F59E0B',
            bio: 'MIT Computer Science & Neuroscience grad. Previously at Microsoft on the Copilot team. Built multi-modal AI at Apple.',
            img: 'radial-gradient(at 40% 25%, #FCD34D, #B45309 60%), linear-gradient(135deg, #78350F, #451A03)',
        },
        {
            name: 'Coming Soon',
            role: 'TBD',
            org: 'TBD',
            slot: 'TBD',
            talk: 'TBD',
            summary: 'TBD',
            tag: 'PANEL',
            tagColor: '#488cec',
            bio: 'TBD',
            img: 'radial-gradient(at 50% 30%, #a8ddf9, #277bdb 60%), linear-gradient(135deg, #831843, #500724)',
        },
        {
            name: 'Coming Soon',
            role: 'TBD',
            org: 'TBD',
            slot: 'TBD',
            talk: 'TBD',
            summary: 'TBD',
            tag: 'PANEL',
            tagColor: '#488cec',
            bio: 'TBD',
            img: 'radial-gradient(at 50% 30%, #a8ddf9, #277bdb 60%), linear-gradient(135deg, #831843, #500724)',
        },
        {
            name: 'Coming Soon',
            role: 'TBD',
            org: 'TBD',
            slot: 'TBD',
            talk: 'TBD',
            summary: 'TBD',
            tag: 'PANEL',
            tagColor: '#488cec',
            bio: 'TBD',
            img: 'radial-gradient(at 50% 30%, #a8ddf9, #277bdb 60%), linear-gradient(135deg, #831843, #500724)',
        },
        {
            name: 'Coming Soon',
            role: 'TBD',
            org: 'TBD',
            slot: 'TBD',
            talk: 'TBD',
            summary: 'TBD',
            tag: 'PANEL',
            tagColor: '#488cec',
            bio: 'TBD',
            img: 'radial-gradient(at 50% 30%, #a8ddf9, #277bdb 60%), linear-gradient(135deg, #831843, #500724)',
        },
    ];

    return (
        <div className="atw-panel">
            <div className="atw-panel-header">
                <div>
                    <div className="atw-eyebrow atw-eyebrow-warm">◆ THE LINEUP</div>
                    <h3 className="atw-subsection-title">
                        The people <em>actually shipping</em><br />
                        this stuff.
                    </h3>
                </div>
                <p className="atw-panel-sub">
                    Every speaker teaches. No pay-to-play keynotes. The remaining panelists drop
                    a week out, with your final confirmation email.
                </p>
            </div>

            <div className="atw-speakers-grid">
                {speakers.map((s, i) => (
                    <SpeakerCard key={s.name} {...s} delay={i * 0.08} />
                ))}
            </div>

        </div>
    );
}

function SpeakerCard({ name, role, org, slot, talk, summary, tag, tagColor, bio, img, delay }) {
    const [open, setOpen] = useState(false);
    return (
        <article className="atw-speaker-card" style={{ animationDelay: `${delay}s` }}>
            <div className="atw-speaker-portrait" style={{ background: img }}>
            </div>
            <div className="atw-speaker-body">
                <div className="atw-speaker-name">{name}</div>
                <div className="atw-speaker-role">
                    {role} · <span className="atw-speaker-org">{org}</span>
                </div>

                <button
                    className="atw-speaker-bio-toggle"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                >
                    {open ? '− Hide bio' : '+ Read bio'}
                </button>
                {open && <div className="atw-speaker-bio">{bio}</div>}
            </div>
        </article>
    );
}

// ═══════════════════════════════════════════════════════
// AGENDA PANEL — half-day only
// ═══════════════════════════════════════════════════════
function AgendaPanel() {
    const schedule = [
        { time: '12:30', dur: '30', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'ARRIVAL', badgeColor: 'neutral' },
        { time: '13:05', dur: '25', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'OPENING', badgeColor: 'rose' },
        { time: '13:45', dur: '40', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'FIRESIDE', badgeColor: 'rose' },
        { time: '14:30', dur: '55', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'PANEL', badgeColor: 'purple' },
        { time: '15:15', dur: '20', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'DEMO', badgeColor: 'gold' },
        { time: '15:40', dur: '20', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'ROUNDTABLE', badgeColor: 'teal' },
        { time: '16:00', dur: '55', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'PANEL', badgeColor: 'purple' },
        { time: '17:00', dur: '25', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'CLOSING', badgeColor: 'rose' },
        { time: '17:30', dur: '—', title: 'TBD', who: 'TBD', desc: 'TBD', badge: 'RECEPTION', badgeColor: 'social' },
    ];

    const sessionCount = schedule.filter(s => !['ARRIVAL', 'ROUNDTABLE', 'RECEPTION'].includes(s.badge)).length;
    const socialCount = schedule.filter(s => ['ARRIVAL', 'ROUNDTABLE', 'RECEPTION'].includes(s.badge)).length;

    return (
        <div className="atw-panel">
            <div className="atw-panel-header">
                <div>
                    <div className="atw-eyebrow atw-eyebrow-warm">◆ ONE AFTERNOON · 9 MOMENTS</div>
                    <h3 className="atw-subsection-title">
                        The program, <em>minute by minute</em>.
                    </h3>
                </div>
                <p className="atw-panel-sub">
                    Every session is on the record for attendees; panels run Chatham House rules.
                    Recordings of keynotes go out 48 hours later.
                </p>
            </div>

            {/* Day summary strip */}
            <div className="atw-day-summary">
                <div className="atw-day-summary-left">
                    <div className="atw-day-summary-label">WED MAY 27 · 2026</div>
                    <h4 className="atw-day-summary-title">AI × Benefits · MIT iHQ</h4>
                </div>
                <div className="atw-day-summary-stats">
                    <div className="atw-day-stat">
                        <span className="atw-day-stat-k">{sessionCount}</span>
                        <span className="atw-day-stat-v">sessions</span>
                    </div>
                    <div className="atw-day-stat">
                        <span className="atw-day-stat-k">2</span>
                        <span className="atw-day-stat-v">panels</span>
                    </div>
                    <div className="atw-day-stat">
                        <span className="atw-day-stat-k">{socialCount}</span>
                        <span className="atw-day-stat-v">social</span>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="atw-timeline">
                <div className="atw-timeline-rail" />
                {schedule.map((item, i) => (
                    <TimelineRow key={i} {...item} index={i} />
                ))}
            </div>
        </div>
    );
}

function TimelineRow({ time, dur, title, who, desc, badge, badgeColor, index }) {
    return (
        <div className="atw-timeline-row" style={{ animationDelay: `${index * 0.06}s` }}>
            <div className="atw-timeline-time">
                <div className="atw-timeline-time-main">{time}</div>
                {dur !== '—' && <div className="atw-timeline-time-dur">{dur} min</div>}
                {dur === '—' && <div className="atw-timeline-time-dur">evening</div>}
            </div>
            <div className="atw-timeline-marker-col">
                <div className={`atw-timeline-marker atw-timeline-marker-${badgeColor}`} />
            </div>
            <div className="atw-timeline-body">
                <div className="atw-timeline-header">
                    <h4 className="atw-timeline-title">{title}</h4>
                    <span className={`atw-timeline-badge atw-timeline-badge-${badgeColor}`}>{badge}</span>
                </div>
                <div className="atw-timeline-who">{who}</div>
                <p className="atw-timeline-desc">{desc}</p>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// SPONSORS PANEL
// ═══════════════════════════════════════════════════════
function SponsorsPanel() {
    return (
        <div className="atw-panel">
            <div className="atw-panel-header">
                <div>
                    <div className="atw-eyebrow atw-eyebrow-warm">◆ PARTNERS & UNDERWRITERS</div>
                    <h3 className="atw-subsection-title">
                        Made possible by <em>partners</em><br />
                        who understand the room.
                    </h3>
                </div>
                <p className="atw-panel-sub">
                    Partnership is what keeps the forum free for attendees. No tradeshow booths,
                    no logo carousels during talks — just the right logos in the right program book.
                </p>
            </div>

            <SponsorTier
                label="Gold Sponsors"
                tier="GOLD"
                note="Program underwriters"
                sponsors={[
                    { name: 'HUB International', tagline: 'Gold sponsor' },
                ]}
                size="xl"
            />

            <SponsorTier
                label="Venue Sponsor"
                tier="LEAD"
                note="Venue partner"
                sponsors={[
                    { name: 'MIT iHQ', tagline: 'Venue sponsor' },
                ]}
                size="l"
            />

            <SponsorTier
                label="Special Thanks"
                tier="COMMUNITY"
                note="With gratitude"
                sponsors={[
                    { name: 'a16z Speedrun' },
                    { name: 'Tech Week' },
                ]}
                size="m"
            />

            <SponsorPitchCard />
        </div>
    );
}

function SponsorTier({ label, tier, note, sponsors, size }) {
    return (
        <div className="atw-sponsor-tier">
            <div className="atw-sponsor-tier-head">
                <div>
                    <div className="atw-sponsor-tier-name">{label}</div>
                    <div className="atw-sponsor-tier-note">{note}</div>
                </div>
                <div className="atw-sponsor-tier-badge">{tier}</div>
            </div>
            <div className={`atw-sponsor-grid atw-sponsor-grid-${size}`}>
                {sponsors.map((s, i) => (
                    <div key={s.name} className="atw-sponsor-tile" style={{ animationDelay: `${i * 0.05}s` }}>
                        {s.logo && <div className="atw-sponsor-logo">{s.logo}</div>}
                        <div className="atw-sponsor-name">{s.name}</div>
                        {s.tagline && <div className="atw-sponsor-tagline">{s.tagline}</div>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function SponsorPitchCard() {
    return (
        <div className="atw-sponsor-pitch">
            <div className="atw-sponsor-pitch-bg" />
            <div className="atw-sponsor-pitch-content">
                <div className="atw-sponsor-pitch-left">
                    <h4 className="atw-sponsor-pitch-title">
                        Your logo, in <em>the right room</em>.
                    </h4>
                    <p className="atw-sponsor-pitch-desc">
                        Partnership is how the forum stays practitioner-weighted and complimentary for
                        attendees. As a sponsor, any of your team receives priority admission, the full attendee list, permanent website visibility, your branding presence on all materials, and priority speaker opportunities.
                    </p>
                    <div className="atw-sponsor-pitch-stats">
                        <div>
                            <div className="atw-sponsor-pitch-stat">50</div>
                            <div className="atw-sponsor-pitch-stat-l">decision-makers</div>
                        </div>
                        <div>
                            <div className="atw-sponsor-pitch-stat">1 day</div>
                            <div className="atw-sponsor-pitch-stat-l">of focused access</div>
                        </div>
                    </div>
                </div>
                <div className="atw-sponsor-pitch-right">
                    <a href="mailto:maria@avenir.com.ai" className="atw-btn atw-btn-ghost atw-btn-large">
                        Ask to become a sponsor
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" />
                        </svg>
                    </a>
                    <div className="atw-sponsor-pitch-contact">
                        maria@avenir.com.ai<br />
                        ari@avenir.com.ai<br />
                    </div>
                </div>
            </div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════
// FAQ PANEL
// ═══════════════════════════════════════════════════════
function FaqPanel({ openFaq, setOpenFaq }) {
    const faqs = [
        {
            q: 'Who is this actually for?',
            a: 'Benefits brokers and consultants making the decisions, HR leaders and CFOs at self-insured employers, and the carriers and strategic operators working alongside them. The room skews 44% brokerage/consulting, 26% employer benefits, 15% carriers/TPAs, with the rest a mix of PE, research, and a small press contingent.',
        },
        {
            q: 'It says invite-only. How do invitations work?',
            a: 'We curate the list by hand. Brokerage principals, senior benefits leaders at self-insured employers (2,000+ lives), carrier strategy and innovation heads, and a small group of researchers and journalists. If you weren\'t sent a direct invitation and think you belong in the room, email us — we\'re happy to consider it, and we turn around requests within a day.',
        },
        {
            q: 'Is it really free?',
            a: 'Yes. The forum is free for invited attendees — all sessions, lunch, coffee, the executive dinner. You don\'t get pitched, you don\'t get sold. You just get the room.',
        },
        {
            q: 'How technical does the content get?',
            a: 'Not technical. This is a strategic forum for decision-makers, not a workshop for engineers. No code on the slides, no jargon in the talks. The live Avenir demo shows the product end-to-end from a broker\'s seat — what a client-facing deliverable looks like — not how it\'s built. If you want the technical deep-dive, we publish it separately on the Avenir blog.',
        },
        {
            q: 'What should I expect to take away?',
            a: 'A clear strategic map of where AI is moving in benefits right now. Two panels of practitioners being unusually honest on the future of benefits. A live look at what a working AI-native product looks like end-to-end. And a small, curated room of peers — some of whom will become long-term sounding boards.',
        },
        {
            q: 'What should I wear?',
            a: 'Business casual — what you\'d wear to a good client meeting. Boston weather in late May is usually excellent but swings 15° between afternoon and night, so bring a layer.',
        }
    ];

    return (
        <div className="atw-panel">
            <div className="atw-faq-layout">
                <div className="atw-faq-intro">
                    <div className="atw-eyebrow atw-eyebrow-warm">◆ EVERYTHING ELSE</div>
                    <h3 className="atw-subsection-title">
                        Questions we <em>actually get asked</em>.
                    </h3>
                    <p className="atw-faq-intro-desc">
                        If yours isn't here, we're a small team and answer every email within one
                        business day. Promise.
                    </p>

                    <div className="atw-faq-contact-card">
                        <div className="atw-faq-contact-label">PING US DIRECTLY</div>
                        <div className="atw-faq-contact-email">maria@avenir.com.ai</div>
                        <div className="atw-faq-contact-email">ari@avenir.com.ai</div>
                        <div className="atw-faq-contact-team">
                            <div className="atw-faq-contact-avatar" style={{ background: 'radial-gradient(at 30% 30%, #FDA4AF, #BE123C 70%)' }}>MZ</div>
                            <div className="atw-faq-contact-avatar" style={{ background: 'radial-gradient(at 40% 30%, #FCD34D, #B45309 70%)' }}>AD</div>
                            <div className="atw-faq-contact-avatar" style={{ background: 'radial-gradient(at 50% 30%, #F9A8D4, #DB2777 70%)' }}>LH</div>
                            <div className="atw-faq-contact-team-text">Avg. response: 6 hrs</div>
                        </div>
                    </div>
                </div>

                <div className="atw-faq-list">
                    {faqs.map((f, i) => (
                        <div
                            key={i}
                            className={`atw-faq-item ${openFaq === i ? 'is-open' : ''}`}
                            onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                        >
                            <button className="atw-faq-question" aria-expanded={openFaq === i}>
                                <span className="atw-faq-q-num">{String(i + 1).padStart(2, '0')}</span>
                                <span className="atw-faq-q-text">{f.q}</span>
                                <span className="atw-faq-toggle" />
                            </button>
                            <div className="atw-faq-answer">
                                <div className="atw-faq-answer-inner">{f.a}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
// FINAL CTA
// ═══════════════════════════════════════════════════════
function FinalCta() {
    return (
        <section className="atw-final-cta">
            <div className="atw-final-cta-inner">
                <div className="atw-final-cta-eyebrow">
                    <span className="atw-final-cta-dot" />
                    12 SEATS OPEN UNTIL CAPACITY
                </div>
                <h2 className="atw-final-cta-title">
                    The <em>room</em> is nearly full.
                </h2>
                <p className="atw-final-cta-sub">
                    50 decision-makers. One afternoon at MIT iHQ. One chance to shape the next
                    ten years of this industry in the same building it started in.
                </p>
                <div className="atw-final-cta-actions">

                    <a href="mailto:maria@avenir.com.ai" className="atw-btn atw-btn-ghost atw-btn-large">
                        Request an invitation
                    </a>
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════
function AtwStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..900;1,9..144,300..900&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

      .atw-root {
        --atw-bg: #1A0810;
        --atw-bg-deep: #0D0409;
        --atw-burgundy: #2D0A14;
        --atw-wine: #450A0A;
        --atw-cream: #F5E6D3;
        --atw-cream-warm: #E8D2B5;
        --atw-ink: #0B0B14;
        --atw-rose: #BE123C;
        --atw-rose-soft: #E11D48;
        --atw-pink: #F472B6;
        --atw-gold: #F59E0B;
        --atw-gold-soft: #FCD34D;
        --atw-teal: #06B6D4;
        --atw-purple: #A855F7;

        font-family: 'Inter', -apple-system, sans-serif;
        background: var(--atw-bg);
        color: var(--atw-cream);
        min-height: 100vh;
        position: relative;
        line-height: 1.5;
        -webkit-font-smoothing: antialiased;
        overflow-x: hidden;
      }

      .atw-root * { box-sizing: border-box; }
      .atw-root button { font-family: inherit; cursor: pointer; border: none; background: none; color: inherit; }
      .atw-root a { color: inherit; text-decoration: none; }

      /* Grain texture overlay */
      .atw-root::before {
        content: '';
        position: fixed; inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");
        opacity: 0.06;
        pointer-events: none;
        z-index: 1000;
        mix-blend-mode: overlay;
      }

      /* ═══════════ BACKDROP ═══════════ */
      .atw-backdrop {
        position: fixed; inset: 0;
        z-index: 0;
        pointer-events: none;
        overflow: hidden;
      }
      .atw-glow {
        position: absolute;
        border-radius: 50%;
        filter: blur(120px);
        opacity: 0.5;
        animation: atw-float 20s ease-in-out infinite;
      }
      .atw-glow-1 {
        width: 700px; height: 700px;
        background: radial-gradient(circle, #7F1D1D 0%, transparent 70%);
        top: -15%; right: -10%;
      }
      .atw-glow-2 {
        width: 600px; height: 600px;
        background: radial-gradient(circle, #831843 0%, transparent 70%);
        bottom: -20%; left: -5%;
        animation-delay: -5s;
      }
      .atw-glow-3 {
        width: 500px; height: 500px;
        background: radial-gradient(circle, #3F0A1F 0%, transparent 70%);
        top: 40%; left: 30%;
        opacity: 0.3;
        animation-delay: -10s;
      }
      .atw-vignette {
        position: absolute; inset: 0;
        background: radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%);
      }

      @keyframes atw-float {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(-30px, 40px) scale(1.08); }
        66% { transform: translate(40px, -30px) scale(0.95); }
      }

      .atw-particles { position: absolute; inset: 0; }
      .atw-particle {
        position: absolute;
        background: radial-gradient(circle, #FCD34D 0%, transparent 70%);
        border-radius: 50%;
        box-shadow: 0 0 8px #FCD34D;
        animation: atw-twinkle ease-in-out infinite;
        opacity: 0;
      }
      @keyframes atw-twinkle {
        0%, 100% { opacity: 0; transform: translateY(0); }
        50% { opacity: 0.85; transform: translateY(-18px); }
      }

      /* ═══════════ NAV ═══════════ */
      .atw-nav {
        position: fixed; top: 0; left: 0; right: 0;
        z-index: 100;
        padding: 1.25rem 2.5rem;
        display: flex; align-items: center; justify-content: space-between;
        background: rgba(13, 4, 9, 0.7);
        backdrop-filter: blur(20px) saturate(160%);
        border-bottom: 1px solid rgba(245, 230, 211, 0.08);
      }
      .atw-nav-brand { display: flex; align-items: center; gap: 0.875rem; }
      .atw-nav-mark {
        width: 32px; height: 32px;
        color: var(--atw-cream);
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, var(--atw-rose), var(--atw-pink));
        border-radius: 8px;
      }
      .atw-nav-mark svg { width: 18px; height: 18px; }
      .atw-nav-wordmark {
        display: flex; align-items: baseline; gap: 0.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        letter-spacing: 0.1em;
      }
      .atw-nav-divider { opacity: 0.3; }
      .atw-nav-wordmark em {
        font-family: 'Fraunces', serif;
        font-style: italic;
        font-weight: 400;
        letter-spacing: -0.01em;
        color: var(--atw-pink);
      }
      .atw-nav-meta { display: flex; align-items: center; gap: 1.5rem; }
      .atw-nav-date {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.75rem;
        opacity: 0.6;
        letter-spacing: 0.05em;
      }
      .atw-nav-pill {
        display: inline-flex; align-items: center; gap: 0.5rem;
        padding: 0.375rem 0.875rem;
        background: rgba(245, 230, 211, 0.08);
        border: 1px solid rgba(245, 230, 211, 0.15);
        border-radius: 999px;
        font-size: 0.75rem;
        letter-spacing: 0.02em;
        font-weight: 500;
      }
      .atw-nav-dot {
        width: 6px; height: 6px;
        background: #4ADE80;
        border-radius: 50%;
        box-shadow: 0 0 8px #4ADE80;
        animation: atw-pulse 2s infinite;
      }
      @keyframes atw-pulse { 0%,100%{opacity:1;} 50%{opacity:0.4;} }

      /* ═══════════ MAIN ═══════════ */
      .atw-main { position: relative; z-index: 1; }

      /* ═══════════ HERO ═══════════ */
      .atw-hero {
        padding: 8rem 2.5rem 5rem;
        position: relative;
      }
      .atw-hero-inner {
        max-width: 1400px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 1.1fr 1fr;
        gap: 4rem;
        align-items: start;
      }

      .atw-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 14px 6px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    font-size: 12px;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.85);
    width: fit-content;
}

.atw-hero-badge-label {
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
}

.atw-hero-badge-divider {
    width: 1px;
    height: 14px;
    background: rgba(255, 255, 255, 0.18);
}

.atw-hero-badge-logo {
    display: block;
    object-fit: contain;
}

/* Tech Week (jpg — likely has a background, so give it a subtle frame) */
.atw-hero-badge-logo-tw {
    height: 20px;
    width: auto;
    border-radius: 4px;
}

/* Avenir (png white — transparent bg, just sits on the pill) */
.atw-hero-badge-logo-av {
    height: 16px;
    width: auto;
}

      .atw-hero-title {
        font-family: 'Fraunces', serif;
        font-size: clamp(5rem, 6.5vw, 5.75rem);
        font-weight: 400;
        line-height: 0.98;
        letter-spacing: -0.035em;
        color: var(--atw-cream);
        margin: 0 0 2rem;
      }
      .atw-hero-title em {
        font-style: italic;
        font-weight: 400;
        background: linear-gradient(105deg, #FDA4AF 0%, #F472B6 50%, #FCD34D 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .atw-hero-date-block { margin-bottom: 1.5rem; }
      .atw-hero-date-main {
        font-family: 'Fraunces', serif;
        font-size: 1.5rem;
        font-weight: 400;
        color: var(--atw-cream);
        margin-bottom: 0.25rem;
      }
      .atw-hero-date-sub {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.875rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
      }

      .atw-hero-actions {
        display: flex; gap: 0.625rem;
        margin-bottom: 2rem;
      }
      .atw-circle-btn {
        width: 44px; height: 44px;
        border-radius: 50%;
        background: rgba(245, 230, 211, 0.08);
        border: 1px solid rgba(245, 230, 211, 0.15);
        color: var(--atw-cream);
        display: flex; align-items: center; justify-content: center;
        transition: all 0.25s ease;
      }
      .atw-circle-btn:hover {
        background: var(--atw-cream);
        color: var(--atw-ink);
        transform: translateY(-2px);
        border-color: var(--atw-cream);
      }
      .atw-circle-btn svg { width: 18px; height: 18px; }

      .atw-hero-host-row { margin-bottom: 2rem; }
      .atw-hero-host-label {
        font-size: 0.8125rem;
        opacity: 0.7;
        margin-bottom: 0.875rem;
        color: var(--atw-cream-warm);
      }
      .atw-hero-hosts {
        display: flex; gap: 2rem; flex-wrap: wrap;
      }
      .atw-host {
        display: flex; align-items: center; gap: 0.75rem;
      }
        .atw-host-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 50%;
}
      .atw-host-avatar {
        width: 40px; height: 40px;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        color: var(--atw-cream);
        font-weight: 600;
        font-size: 0.8125rem;
        font-family: 'JetBrains Mono', monospace;
        position: relative;
        box-shadow: 0 4px 16px rgba(0,0,0,0.4);
      }
      .atw-host-initials {
        text-shadow: 0 1px 2px rgba(0,0,0,0.4);
      }
      .atw-host-info {}
      .atw-host-name {
        font-weight: 600;
        font-size: 0.9375rem;
        color: var(--atw-cream);
      }
      .atw-host-role {
        font-size: 0.75rem;
        opacity: 0.55;
      }

      .atw-hero-info-list {
        display: flex; flex-direction: column; gap: 0.875rem;
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid rgba(245, 230, 211, 0.1);
      }
      .atw-hero-info {
        display: flex; align-items: flex-start; gap: 0.75rem;
        color: var(--atw-cream-warm);
      }
      .atw-hero-info svg {
        margin-top: 2px;
        opacity: 0.7;
        flex-shrink: 0;
      }
      .atw-info-primary {
        font-size: 0.9375rem;
        font-weight: 500;
        color: var(--atw-cream);
        margin-bottom: 0.1875rem;
      }
      .atw-info-secondary {
        font-size: 0.8125rem;
        opacity: 0.55;
      }

      .atw-hero-desc {
        font-size: 1rem;
        line-height: 1.7;
        color: var(--atw-cream-warm);
        opacity: 0.85;
        margin-bottom: 1.25rem;
        max-width: 36rem;
      }
      .atw-hero-desc em {
        font-family: 'Fraunces', serif;
        font-style: italic;
        color: var(--atw-pink);
        font-weight: 400;
      }

      /* Hero right column */
      .atw-hero-right {
        display: flex; flex-direction: column; gap: 1.25rem;
        position: sticky; top: 6rem;
      }

      /* Hero artwork */
      .atw-art {
        border-radius: 20px;
        overflow: hidden;
        background: #0D0409;
        border: 1px solid rgba(245, 230, 211, 0.1);
        box-shadow: 0 20px 60px -20px rgba(0,0,0,0.8);
      }
      .atw-art-frame { aspect-ratio: 1/1; }
      .atw-art-svg { width: 100%; height: 100%; display: block; }

      /* Artwork animations */
      .atw-art-row {
        transform-origin: left center;
        animation: atw-rowScan 6s ease-in-out infinite;
      }
      @keyframes atw-rowScan {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 1; }
      }
      .atw-art-conn {
        stroke-dasharray: 100;
        stroke-dashoffset: 100;
        animation: atw-connDraw 4s ease-in-out infinite;
      }
      @keyframes atw-connDraw {
        0% { stroke-dashoffset: 100; opacity: 0; }
        20%, 80% { stroke-dashoffset: 0; opacity: 0.4; }
        100% { stroke-dashoffset: -100; opacity: 0; }
      }
      .atw-art-node-pulse {
        animation: atw-nodePulse 2.5s ease-in-out infinite;
        transform-origin: center;
      }
      @keyframes atw-nodePulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.5); opacity: 0.6; }
      }
      .atw-art-bar {
        animation: atw-barGrow 1.2s cubic-bezier(0.2, 0.9, 0.3, 1) backwards;
      }
      @keyframes atw-barGrow {
        from { transform: scaleX(0); }
        to { transform: scaleX(1); }
      }
      .atw-art-chart-line {
        stroke-dasharray: 200;
        stroke-dashoffset: 200;
        animation: atw-lineDraw 2.5s ease-out 1.8s forwards;
      }
      @keyframes atw-lineDraw {
        to { stroke-dashoffset: 0; }
      }
      .atw-art-chart-area {
        opacity: 0;
        animation: atw-areaFadeIn 1s ease-out 3s forwards;
      }
      @keyframes atw-areaFadeIn {
        to { opacity: 1; }
      }
      .atw-art-chart-dot {
        opacity: 0;
        animation: atw-dotFadeIn 0.4s ease-out forwards;
      }
      @keyframes atw-dotFadeIn {
        to { opacity: 1; }
      }

      /* RSVP row */
      .atw-rsvp-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75rem;
      }
      .atw-rsvp-btn {
        display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
        padding: 1rem 0.5rem;
        background: radial-gradient(at 30% 30%, rgba(245, 230, 211, 0.12), rgba(245, 230, 211, 0.03));
        border: 1px solid rgba(245, 230, 211, 0.12);
        border-radius: 14px;
        transition: all 0.25s ease;
        color: var(--atw-cream);
      }
      .atw-rsvp-btn:hover {
        background: radial-gradient(at 30% 30%, rgba(245, 230, 211, 0.2), rgba(245, 230, 211, 0.05));
        transform: translateY(-3px);
        border-color: rgba(245, 230, 211, 0.3);
      }
      .atw-rsvp-btn.is-active {
        background: radial-gradient(at 30% 30%, var(--atw-rose), var(--atw-wine));
        border-color: var(--atw-pink);
        box-shadow: 0 8px 30px -8px rgba(244, 114, 182, 0.5);
      }
      .atw-rsvp-emoji {
        font-size: 1.5rem;
        font-family: 'Fraunces', serif;
        color: var(--atw-gold-soft);
      }
      .atw-rsvp-btn.is-active .atw-rsvp-emoji { color: var(--atw-cream); }
      .atw-rsvp-label {
        font-size: 0.8125rem;
        font-weight: 500;
        letter-spacing: 0.02em;
      }

      .atw-rsvp-confirm {
        display: flex; align-items: center; gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: rgba(74, 222, 128, 0.1);
        border: 1px solid rgba(74, 222, 128, 0.3);
        border-radius: 12px;
        font-size: 0.875rem;
        color: #86EFAC;
        animation: atw-slideDown 0.3s ease;
      }
      @keyframes atw-slideDown {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* Countdown */
      .atw-countdown {
        padding: 1.25rem;
        background: linear-gradient(135deg, rgba(190, 18, 60, 0.15), rgba(66, 10, 20, 0.4));
        border: 1px solid rgba(244, 114, 182, 0.15);
        border-radius: 16px;
      }
      .atw-countdown-label {
        font-size: 0.625rem;
        letter-spacing: 0.18em;
        font-weight: 600;
        color: var(--atw-pink);
        margin-bottom: 0.75rem;
      }
      .atw-countdown-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 0.5rem;
      }
      .atw-count-unit {
        text-align: center;
        padding: 0.75rem 0.25rem;
        background: rgba(0,0,0,0.25);
        border-radius: 10px;
        border: 1px solid rgba(245, 230, 211, 0.06);
      }
      .atw-count-val {
        font-family: 'Fraunces', serif;
        font-size: 1.75rem;
        font-weight: 400;
        line-height: 1;
        color: var(--atw-cream);
        font-variant-numeric: tabular-nums;
      }
      .atw-count-lbl {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.625rem;
        color: var(--atw-cream-warm);
        opacity: 0.5;
        margin-top: 0.375rem;
        letter-spacing: 0.1em;
      }

      /* Live strip */
      .atw-live-strip {
        display: flex; align-items: center; gap: 0.75rem;
        padding: 0.875rem 1rem;
        background: rgba(245, 230, 211, 0.04);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 12px;
      }
      .atw-live-avatars {
        display: flex; align-items: center;
      }
      .atw-live-avatar {
        width: 28px; height: 28px;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--atw-cream);
        border: 2px solid var(--atw-bg);
        margin-left: -8px;
      }
      .atw-live-avatar:first-child { margin-left: 0; }
      .atw-live-more {
        margin-left: -8px;
        width: 28px; height: 28px;
        border-radius: 50%;
        background: rgba(245, 230, 211, 0.15);
        border: 2px solid var(--atw-bg);
        font-size: 0.625rem;
        font-weight: 600;
        display: flex; align-items: center; justify-content: center;
      }
      .atw-live-text {
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
      }
      .atw-live-text strong { color: var(--atw-cream); font-weight: 500; }

      @media (max-width: 1024px) {
        .atw-hero { padding: 7rem 1.5rem 4rem; }
        .atw-hero-inner { grid-template-columns: 1fr; gap: 3rem; }
        .atw-hero-right { position: static; }
      }

      /* ═══════════ TOAST ═══════════ */
      .atw-toast {
        position: fixed;
        bottom: 2rem; left: 50%;
        transform: translateX(-50%);
        z-index: 200;
        padding: 0.75rem 1.25rem;
        background: var(--atw-cream);
        color: var(--atw-ink);
        border-radius: 999px;
        font-size: 0.875rem;
        font-weight: 500;
        display: inline-flex; align-items: center; gap: 0.5rem;
        box-shadow: 0 20px 40px -10px rgba(0,0,0,0.5);
        animation: atw-toastIn 0.3s cubic-bezier(0.2, 0.9, 0.3, 1);
      }
      @keyframes atw-toastIn {
        from { opacity: 0; transform: translate(-50%, 20px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }

      /* ═══════════ MARQUEE ═══════════ */
      .atw-marquee {
        padding: 1.5rem 0;
        border-top: 1px solid rgba(245, 230, 211, 0.08);
        border-bottom: 1px solid rgba(245, 230, 211, 0.08);
        background: linear-gradient(90deg, rgba(190, 18, 60, 0.06), rgba(66, 10, 20, 0.1));
        overflow: hidden;
      }
      .atw-marquee-track {
        display: flex;
        gap: 3rem;
        animation: atw-marquee 40s linear infinite;
        white-space: nowrap;
      }
      @keyframes atw-marquee {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .atw-marquee-item {
        display: inline-flex; align-items: center; gap: 1rem;
        font-family: 'Fraunces', serif;
        font-size: 1.375rem;
        font-weight: 300;
        font-style: italic;
        color: var(--atw-cream);
        opacity: 0.75;
      }
      .atw-marquee-star {
        color: var(--atw-pink);
        font-size: 0.75rem;
        font-style: normal;
      }

      /* ═══════════ TABS SECTION ═══════════ */
      .atw-tabs-section {
        max-width: 1400px;
        margin: 0 auto;
        padding: 6rem 2.5rem;
      }

      .atw-tabs-header {
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        gap: 3rem;
        align-items: end;
        margin-bottom: 3rem;
      }
      .atw-eyebrow {
        display: inline-block;
        font-size: 0.6875rem;
        letter-spacing: 0.18em;
        font-weight: 600;
        color: var(--atw-pink);
        margin-bottom: 1.25rem;
      }
      .atw-eyebrow-warm { color: var(--atw-gold-soft); }

      .atw-section-title {
        font-family: 'Fraunces', serif;
        font-size: clamp(2.25rem, 4.5vw, 3.5rem);
        font-weight: 400;
        line-height: 1.02;
        letter-spacing: -0.03em;
        color: var(--atw-cream);
        margin: 0;
      }
      .atw-section-title em {
        font-style: italic;
        color: var(--atw-pink);
        font-weight: 400;
      }

      .atw-section-sub {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        line-height: 1.6;
        max-width: 22rem;
      }

      /* Tab list */
      .atw-tablist {
        display: flex;
        gap: 0;
        border-bottom: 1px solid rgba(245, 230, 211, 0.1);
        margin-bottom: 3rem;
        overflow-x: auto;
        scrollbar-width: none;
      }
      .atw-tablist::-webkit-scrollbar { display: none; }

      .atw-tab {
        display: inline-flex; align-items: center; gap: 0.75rem;
        padding: 1.125rem 1.5rem;
        font-size: 0.9375rem;
        font-weight: 500;
        color: rgba(245, 230, 211, 0.45);
        white-space: nowrap;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        transition: all 0.25s ease;
        position: relative;
      }
      .atw-tab-num {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        opacity: 0.6;
        letter-spacing: 0.05em;
      }
      .atw-tab:hover { color: var(--atw-cream); }
      .atw-tab.is-active {
        color: var(--atw-cream);
        border-bottom-color: var(--atw-pink);
      }
      .atw-tab.is-active .atw-tab-num { color: var(--atw-pink); opacity: 1; }

      /* Panel */
      .atw-panel {
        animation: atw-fadeSlide 0.5s ease;
      }
      @keyframes atw-fadeSlide {
        from { opacity: 0; transform: translateY(14px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .atw-panel-header {
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        gap: 3rem;
        align-items: end;
        margin-bottom: 3rem;
      }
      .atw-subsection-title {
        font-family: 'Fraunces', serif;
        font-size: clamp(1.75rem, 3.5vw, 2.5rem);
        font-weight: 400;
        line-height: 1.1;
        letter-spacing: -0.025em;
        color: var(--atw-cream);
        margin: 0;
      }
      .atw-subsection-title em {
        font-style: italic;
        color: var(--atw-pink);
      }
      .atw-panel-sub {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        line-height: 1.6;
        max-width: 24rem;
      }

      @media (max-width: 900px) {
        .atw-tabs-section { padding: 4rem 1.5rem; }
        .atw-tabs-header, .atw-panel-header { grid-template-columns: 1fr; gap: 1rem; }
      }

      /* ═══════════ ABOUT PANEL ═══════════ */
      .atw-about-hero {
        display: grid;
        grid-template-columns: 1.5fr 1fr;
        gap: 4rem;
        align-items: center;
        margin-bottom: 4rem;
      }
      .atw-about-lead {
        position: relative;
        padding-left: 2rem;
      }
      .atw-about-quote-mark {
        position: absolute;
        left: -0.25rem; top: -2.5rem;
        font-family: 'Fraunces', serif;
        font-size: 10rem;
        line-height: 1;
        color: var(--atw-rose);
        opacity: 0.4;
        font-weight: 400;
      }
      .atw-about-lead p {
        font-family: 'Fraunces', serif;
        font-size: clamp(1.5rem, 2.5vw, 2rem);
        font-weight: 300;
        line-height: 1.35;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        margin: 0 0 1.5rem;
      }
      .atw-about-lead em {
        font-style: italic;
        background: linear-gradient(105deg, var(--atw-pink), var(--atw-gold));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        font-weight: 400;
      }
      .atw-about-attrib {
        font-size: 0.875rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
        font-style: italic;
      }

      .atw-about-stats {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
      }
      .atw-statblock {
        padding: 1.5rem;
        background: rgba(245, 230, 211, 0.03);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 14px;
        transition: all 0.3s ease;
      }
      .atw-statblock:hover {
        background: rgba(245, 230, 211, 0.06);
        border-color: rgba(244, 114, 182, 0.3);
        transform: translateY(-3px);
      }
      .atw-statblock-val {
        font-family: 'Fraunces', serif;
        font-size: 2rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        background: linear-gradient(105deg, var(--atw-cream), var(--atw-pink));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        line-height: 1;
      }
      .atw-statblock-lbl {
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.65;
        margin-top: 0.5rem;
        line-height: 1.4;
      }

      .atw-about-divider {
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(244, 114, 182, 0.3), transparent);
        margin: 4rem 0;
      }

      /* Pillars */
      .atw-pillars {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.25rem;
        margin-bottom: 5rem;
      }
      .atw-pillar {
        --atw-pillar-accent: var(--atw-pink);
        padding: 2rem;
        background: rgba(245, 230, 211, 0.03);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 20px;
        transition: all 0.4s ease;
        position: relative;
        overflow: hidden;
        display: flex; flex-direction: column;
        gap: 1.5rem;
      }
      .atw-pillar::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 3px;
        background: var(--atw-pillar-accent);
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.5s ease;
      }
      .atw-pillar:hover {
        transform: translateY(-6px);
        border-color: rgba(245, 230, 211, 0.2);
        background: rgba(245, 230, 211, 0.05);
      }
      .atw-pillar:hover::before { transform: scaleX(1); }
      .atw-pillar-num {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        color: var(--atw-pillar-accent);
        letter-spacing: 0.1em;
        font-weight: 600;
      }
      .atw-pillar-title {
        font-family: 'Fraunces', serif;
        font-size: 1.5rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        margin: 0.5rem 0 0.75rem;
        line-height: 1.2;
      }
      .atw-pillar-desc {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.75;
        line-height: 1.6;
        margin: 0;
      }
      .atw-pillar-stats {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75rem;
        padding-top: 1.5rem;
        border-top: 1px solid rgba(245, 230, 211, 0.08);
      }
      .atw-pillar-stat-k {
        font-family: 'Fraunces', serif;
        font-size: 1.5rem;
        color: var(--atw-cream);
        line-height: 1;
      }
      .atw-pillar-stat-v {
        font-size: 0.6875rem;
        color: var(--atw-cream-warm);
        opacity: 0.55;
        margin-top: 0.25rem;
        line-height: 1.3;
      }

      /* Attendee breakdown */
      .atw-breakdown {
        padding: 2.5rem;
        background: linear-gradient(135deg, rgba(190, 18, 60, 0.08), rgba(66, 10, 20, 0.2));
        border: 1px solid rgba(244, 114, 182, 0.15);
        border-radius: 24px;
        overflow: hidden;
      }
      .atw-breakdown-header { margin-bottom: 2.5rem; }
      .atw-breakdown-viz { }
      .atw-breakdown-bar {
        display: flex;
        height: 56px;
        border-radius: 12px;
        overflow: hidden;
        margin-bottom: 2rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.4);
      }
      .atw-breakdown-seg {
        height: 100%;
        transition: all 0.3s ease;
        transform-origin: left;
        animation: atw-growBarH 1s cubic-bezier(0.2, 0.9, 0.3, 1) forwards;
        transform: scaleX(0);
        cursor: pointer;
      }
      .atw-breakdown-seg:hover {
        transform: scaleY(1.1) translateY(-2px) !important;
      }
      @keyframes atw-growBarH {
        to { transform: scaleX(1); }
      }
      .atw-breakdown-legend {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75rem 1.5rem;
      }
      .atw-breakdown-item {
        display: grid;
        grid-template-columns: 12px 1fr auto;
        gap: 0.75rem;
        align-items: center;
      }
      .atw-breakdown-sq { width: 12px; height: 12px; border-radius: 3px; }
      .atw-breakdown-label {
        font-size: 0.875rem;
        color: var(--atw-cream);
      }
      .atw-breakdown-pct {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
      }

      @media (max-width: 900px) {
        .atw-about-hero { grid-template-columns: 1fr; gap: 2rem; }
        .atw-pillars { grid-template-columns: 1fr; }
        .atw-breakdown-legend { grid-template-columns: 1fr; }
        .atw-about-stats { grid-template-columns: 1fr; }
      }

      /* ═══════════ SPEAKERS ═══════════ */
      .atw-speakers-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
      }
      .atw-speaker-card {
        background: rgba(245, 230, 211, 0.03);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 18px;
        overflow: hidden;
        transition: all 0.4s cubic-bezier(0.2, 0.9, 0.3, 1);
        animation: atw-fadeUp 0.6s ease backwards;
      }
      @keyframes atw-fadeUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .atw-speaker-card:hover {
        transform: translateY(-6px);
        border-color: rgba(244, 114, 182, 0.25);
        box-shadow: 0 30px 60px -20px rgba(0,0,0,0.6);
      }
      .atw-speaker-portrait {
        aspect-ratio: 4/3;
        position: relative;
        background-size: cover;
        background-position: center;
      }
      .atw-speaker-card:hover .atw-speaker-portrait {
        filter: brightness(1.1);
      }
      .atw-speaker-tag {
        position: absolute;
        top: 1rem; left: 1rem;
        padding: 0.3125rem 0.625rem;
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        border-radius: 999px;
        color: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      }
      .atw-speaker-slot {
        position: absolute;
        bottom: 1rem; left: 1rem; right: 1rem;
        padding: 0.5rem 0.75rem;
        background: rgba(11, 11, 20, 0.8);
        backdrop-filter: blur(8px);
        border-radius: 8px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        color: var(--atw-cream);
        letter-spacing: 0.02em;
      }
      .atw-speaker-body { padding: 1.5rem; }
      .atw-speaker-name {
        font-family: 'Fraunces', serif;
        font-size: 1.375rem;
        font-weight: 400;
        letter-spacing: -0.01em;
        color: var(--atw-cream);
        margin-bottom: 0.25rem;
      }
      .atw-speaker-role {
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        margin-bottom: 1.25rem;
      }
      .atw-speaker-org {
        color: var(--atw-pink);
        font-weight: 500;
      }
      .atw-speaker-talk {
        padding: 1rem;
        background: rgba(0,0,0,0.2);
        border-radius: 10px;
        border-left: 3px solid var(--atw-pink);
        margin-bottom: 1rem;
      }
      .atw-speaker-talk-label {
        font-size: 0.625rem;
        letter-spacing: 0.15em;
        font-weight: 700;
        color: var(--atw-pink);
        margin-bottom: 0.375rem;
      }
      .atw-speaker-talk-title {
        font-family: 'Fraunces', serif;
        font-size: 1.0625rem;
        font-style: italic;
        color: var(--atw-cream);
        margin-bottom: 0.625rem;
        line-height: 1.3;
      }
      .atw-speaker-summary {
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.75;
        line-height: 1.55;
        margin: 0;
      }
      .atw-speaker-bio-toggle {
        font-size: 0.75rem;
        color: var(--atw-pink);
        font-weight: 500;
        letter-spacing: 0.02em;
      }
      .atw-speaker-bio {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px solid rgba(245, 230, 211, 0.08);
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.75;
        line-height: 1.6;
        animation: atw-fadeSlide 0.3s ease;
      }

      .atw-speakers-more {
        display: flex; align-items: center; gap: 1.5rem;
        margin-top: 3rem;
      }
      .atw-speakers-more-line {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(245, 230, 211, 0.2), transparent);
      }
      .atw-speakers-more-text {
        font-family: 'Fraunces', serif;
        font-size: 1.125rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        font-style: italic;
      }
      .atw-speakers-more em {
        color: var(--atw-pink);
        font-weight: 400;
      }

      @media (max-width: 900px) { .atw-speakers-grid { grid-template-columns: 1fr; } }
      @media (min-width: 600px) and (max-width: 900px) { .atw-speakers-grid { grid-template-columns: repeat(2, 1fr); } }

      /* ═══════════ AGENDA ═══════════ */
      .atw-day-summary {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 2rem;
        align-items: center;
        padding: 1.5rem 2rem;
        background: rgba(0,0,0,0.25);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 16px;
        margin-bottom: 2.5rem;
      }
      .atw-day-summary-label {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        color: var(--atw-pink);
        letter-spacing: 0.1em;
        font-weight: 600;
        margin-bottom: 0.25rem;
      }
      .atw-day-summary-title {
        font-family: 'Fraunces', serif;
        font-size: 1.5rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        margin: 0;
      }
      .atw-day-summary-stats { display: flex; gap: 2rem; }
      .atw-day-stat { text-align: center; }
      .atw-day-stat-k {
        font-family: 'Fraunces', serif;
        font-size: 1.75rem;
        color: var(--atw-pink);
        display: block;
        line-height: 1;
      }
      .atw-day-stat-v {
        font-size: 0.6875rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin-top: 0.375rem;
        display: block;
      }

      /* Timeline */
      .atw-timeline {
        position: relative;
        padding-left: 0;
      }
      .atw-timeline-rail {
        position: absolute;
        left: 96px;
        top: 0; bottom: 0;
        width: 2px;
        background: linear-gradient(180deg,
          rgba(244, 114, 182, 0.4) 0%,
          rgba(244, 114, 182, 0.15) 100%);
      }
      .atw-timeline-row {
        display: grid;
        grid-template-columns: 80px 32px 1fr;
        gap: 1rem;
        padding: 1.5rem 0;
        border-bottom: 1px solid rgba(245, 230, 211, 0.05);
        animation: atw-fadeUp 0.5s ease backwards;
      }
      .atw-timeline-row:last-child { border-bottom: none; }
      .atw-timeline-time { text-align: right; padding-top: 0.25rem; }
      .atw-timeline-time-main {
        font-family: 'JetBrains Mono', monospace;
        font-size: 1rem;
        color: var(--atw-cream);
        font-weight: 600;
      }
      .atw-timeline-time-dur {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        color: var(--atw-cream-warm);
        opacity: 0.5;
        margin-top: 0.25rem;
      }
      .atw-timeline-marker-col {
        display: flex; justify-content: center;
        padding-top: 0.375rem;
      }
      .atw-timeline-marker {
        width: 14px; height: 14px;
        border-radius: 50%;
        border: 3px solid var(--atw-bg);
        box-shadow: 0 0 0 2px rgba(244, 114, 182, 0.5);
        z-index: 1;
      }
      .atw-timeline-marker-rose { background: var(--atw-rose); box-shadow: 0 0 0 2px var(--atw-rose), 0 0 16px var(--atw-rose); }
      .atw-timeline-marker-gold { background: var(--atw-gold); box-shadow: 0 0 0 2px var(--atw-gold), 0 0 16px var(--atw-gold); }
      .atw-timeline-marker-purple { background: var(--atw-purple); box-shadow: 0 0 0 2px var(--atw-purple), 0 0 16px var(--atw-purple); }
      .atw-timeline-marker-teal { background: var(--atw-teal); box-shadow: 0 0 0 2px var(--atw-teal), 0 0 16px var(--atw-teal); }
      .atw-timeline-marker-social { background: var(--atw-pink); box-shadow: 0 0 0 2px var(--atw-pink), 0 0 16px var(--atw-pink); }
      .atw-timeline-marker-neutral { background: rgba(245, 230, 211, 0.4); box-shadow: 0 0 0 2px rgba(245, 230, 211, 0.4); }
      .atw-timeline-body {}
      .atw-timeline-header {
        display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;
        margin-bottom: 0.375rem;
      }
      .atw-timeline-title {
        font-family: 'Fraunces', serif;
        font-size: 1.25rem;
        font-weight: 400;
        letter-spacing: -0.01em;
        color: var(--atw-cream);
        line-height: 1.3;
        margin: 0;
      }
      .atw-timeline-badge {
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        padding: 0.3125rem 0.625rem;
        border-radius: 999px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      .atw-timeline-badge-rose { background: rgba(190, 18, 60, 0.2); color: #FDA4AF; border: 1px solid rgba(190, 18, 60, 0.4); }
      .atw-timeline-badge-gold { background: rgba(245, 158, 11, 0.15); color: #FCD34D; border: 1px solid rgba(245, 158, 11, 0.35); }
      .atw-timeline-badge-purple { background: rgba(168, 85, 247, 0.15); color: #C4B5FD; border: 1px solid rgba(168, 85, 247, 0.35); }
      .atw-timeline-badge-teal { background: rgba(6, 182, 212, 0.15); color: #7DD3FC; border: 1px solid rgba(6, 182, 212, 0.35); }
      .atw-timeline-badge-social { background: rgba(244, 114, 182, 0.15); color: #F9A8D4; border: 1px solid rgba(244, 114, 182, 0.35); }
      .atw-timeline-badge-neutral { background: rgba(245, 230, 211, 0.08); color: var(--atw-cream-warm); border: 1px solid rgba(245, 230, 211, 0.15); }

      .atw-timeline-who {
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        margin-bottom: 0.5rem;
        font-style: italic;
      }
      .atw-timeline-desc {
        font-size: 0.875rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
        line-height: 1.55;
        margin: 0;
        max-width: 42rem;
      }

      @media (max-width: 760px) {
        .atw-day-summary { grid-template-columns: 1fr; }
        .atw-day-summary-stats { gap: 1rem; }
        .atw-timeline-rail { left: 50px; }
        .atw-timeline-row { grid-template-columns: 50px 20px 1fr; gap: 0.5rem; }
        .atw-timeline-time-main { font-size: 0.8125rem; }
        .atw-timeline-header { flex-direction: column; }
      }

      /* ═══════════ SPONSORS ═══════════ */
      .atw-sponsor-tier { margin-bottom: 3rem; }
      .atw-sponsor-tier-head {
        display: flex; justify-content: space-between; align-items: baseline;
        gap: 1rem;
        padding-bottom: 0.875rem;
        border-bottom: 1px solid rgba(245, 230, 211, 0.1);
        margin-bottom: 1.5rem;
      }
      .atw-sponsor-tier-name {
        font-family: 'Fraunces', serif;
        font-size: 1.125rem;
        color: var(--atw-cream);
        margin-bottom: 0.125rem;
      }
      .atw-sponsor-tier-note {
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.55;
      }
      .atw-sponsor-tier-badge {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.6875rem;
        letter-spacing: 0.08em;
        color: var(--atw-pink);
        padding: 0.3125rem 0.625rem;
        border: 1px solid var(--atw-pink);
        border-radius: 999px;
        white-space: nowrap;
      }

      .atw-sponsor-grid {
        display: grid;
        gap: 0.875rem;
      }
      .atw-sponsor-grid-xl { grid-template-columns: repeat(2, 1fr); }
      .atw-sponsor-grid-l { grid-template-columns: repeat(4, 1fr); }
      .atw-sponsor-grid-m { grid-template-columns: repeat(6, 1fr); }
      .atw-sponsor-grid-s { grid-template-columns: repeat(4, 1fr); }

      .atw-sponsor-tile {
        padding: 1.5rem;
        background: rgba(245, 230, 211, 0.03);
        border: 1px solid rgba(245, 230, 211, 0.08);
        border-radius: 14px;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center;
        transition: all 0.3s ease;
        min-height: 100px;
        animation: atw-fadeUp 0.5s ease backwards;
      }
      .atw-sponsor-tile:hover {
        background: rgba(245, 230, 211, 0.08);
        border-color: rgba(244, 114, 182, 0.3);
        transform: translateY(-4px);
      }
      .atw-sponsor-logo {
        color: var(--atw-cream);
        margin-bottom: 0.75rem;
        opacity: 0.85;
        transition: all 0.3s ease;
      }
      .atw-sponsor-tile:hover .atw-sponsor-logo { color: var(--atw-pink); opacity: 1; }
      .atw-sponsor-grid-xl .atw-sponsor-tile { padding: 2.5rem; min-height: 180px; }
      .atw-sponsor-grid-l .atw-sponsor-tile { min-height: 130px; }
      .atw-sponsor-grid-m .atw-sponsor-tile { min-height: 90px; }
      .atw-sponsor-grid-s .atw-sponsor-tile { padding: 1rem; min-height: 70px; }

      .atw-sponsor-name {
        font-family: 'Fraunces', serif;
        font-size: 1rem;
        font-weight: 400;
        color: var(--atw-cream);
        letter-spacing: -0.01em;
      }
      .atw-sponsor-grid-xl .atw-sponsor-name { font-size: 1.75rem; }
      .atw-sponsor-grid-l .atw-sponsor-name { font-size: 1.25rem; }
      .atw-sponsor-grid-s .atw-sponsor-name { font-size: 0.875rem; }

      .atw-sponsor-tagline {
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
        margin-top: 0.375rem;
      }

      .atw-logo-simple {
        width: 40px; height: 40px;
        border-radius: 10px;
        background: linear-gradient(135deg, var(--atw-rose), var(--atw-wine));
        display: flex; align-items: center; justify-content: center;
        font-family: 'Fraunces', serif;
        font-size: 1.25rem;
        color: var(--atw-cream);
      }

      /* Sponsor pitch card */
      .atw-sponsor-pitch {
        position: relative;
        margin-top: 4rem;
        padding: 3rem;
        background: linear-gradient(135deg, var(--atw-burgundy), var(--atw-wine));
        border-radius: 24px;
        overflow: hidden;
        border: 1px solid rgba(244, 114, 182, 0.2);
      }
      .atw-sponsor-pitch-bg {
        position: absolute;
        top: -30%; right: -20%;
        width: 500px; height: 500px;
        background: radial-gradient(circle, rgba(244, 114, 182, 0.3), transparent 70%);
        filter: blur(40px);
      }
      .atw-sponsor-pitch-content {
        position: relative;
        z-index: 1;
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        gap: 2rem;
        align-items: center;
      }
      .atw-sponsor-pitch-title {
        font-family: 'Fraunces', serif;
        font-size: 2rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        margin: 0.5rem 0 0.75rem;
        line-height: 1.1;
      }
      .atw-sponsor-pitch-title em {
        font-style: italic;
        color: var(--atw-pink);
      }
      .atw-sponsor-pitch-desc {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.85;
        line-height: 1.6;
        margin-bottom: 1.5rem;
      }
      .atw-sponsor-pitch-stats {
        display: grid; grid-template-columns: repeat(3, auto); gap: 2rem;
      }
      .atw-sponsor-pitch-stat {
        font-family: 'Fraunces', serif;
        font-size: 1.75rem;
        color: var(--atw-cream);
      }
      .atw-sponsor-pitch-stat-l {
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.6;
        margin-top: 0.25rem;
      }
      .atw-sponsor-pitch-right {
        display: flex; flex-direction: column; gap: 1rem; align-items: flex-start;
      }
      .atw-sponsor-pitch-contact {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.65;
        line-height: 1.5;
      }

      @media (max-width: 900px) {
        .atw-sponsor-grid-xl { grid-template-columns: 1fr; }
        .atw-sponsor-grid-l { grid-template-columns: repeat(2, 1fr); }
        .atw-sponsor-grid-m, .atw-sponsor-grid-s { grid-template-columns: repeat(2, 1fr); }
        .atw-sponsor-pitch { padding: 2rem; }
        .atw-sponsor-pitch-content { grid-template-columns: 1fr; }
      }

      /* ═══════════ PRE-EVENT ═══════════ */
      .atw-preevent-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.25rem;
        margin-bottom: 4rem;
      }
      .atw-preevent-card {
        padding: 2rem;
        border-radius: 22px;
        position: relative;
        overflow: hidden;
        display: flex; flex-direction: column;
        min-height: 360px;
        transition: all 0.4s cubic-bezier(0.2, 0.9, 0.3, 1);
        border: 1px solid rgba(245, 230, 211, 0.1);
        animation: atw-fadeUp 0.6s ease backwards;
      }
      .atw-preevent-card::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        opacity: 0.85;
        z-index: 0;
      }
      .atw-preevent-warm::before { background: linear-gradient(135deg, #78350F, #451A03); }
      .atw-preevent-cool::before { background: linear-gradient(135deg, #1E1B4B, #0F172A); }
      .atw-preevent-rose::before { background: linear-gradient(135deg, #831843, #450A0A); }
      .atw-preevent-gold::before { background: linear-gradient(135deg, #713F12, #422006); }

      .atw-preevent-card > * { position: relative; z-index: 1; }
      .atw-preevent-card:hover {
        transform: translateY(-8px);
        border-color: rgba(244, 114, 182, 0.4);
        box-shadow: 0 30px 60px -20px rgba(0,0,0,0.6);
      }
      .atw-preevent-head {
        display: flex; justify-content: space-between; align-items: flex-start;
        margin-bottom: 1.5rem;
      }
      .atw-preevent-icon-wrap {
        width: 56px; height: 56px;
        border-radius: 14px;
        background: rgba(245, 230, 211, 0.12);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(245, 230, 211, 0.2);
        display: flex; align-items: center; justify-content: center;
        font-size: 1.75rem;
      }
      .atw-preevent-icon { animation: atw-wobble 5s ease-in-out infinite; display: inline-block; }
      @keyframes atw-wobble {
        0%, 100% { transform: rotate(0); }
        25% { transform: rotate(-8deg); }
        75% { transform: rotate(8deg); }
      }
      .atw-preevent-price {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        padding: 0.375rem 0.75rem;
        background: rgba(245, 230, 211, 0.15);
        border: 1px solid rgba(245, 230, 211, 0.25);
        border-radius: 999px;
        color: var(--atw-cream);
      }
      .atw-preevent-date {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
      }
      .atw-preevent-title {
        font-family: 'Fraunces', serif;
        font-size: 1.625rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        line-height: 1.15;
        margin: 0 0 0.625rem;
      }
      .atw-preevent-venue {
        display: inline-flex; align-items: center; gap: 0.375rem;
        font-size: 0.8125rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
        margin-bottom: 0.875rem;
      }
      .atw-preevent-desc {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.85;
        line-height: 1.55;
        margin: 0 0 auto;
        flex: 1;
      }
      .atw-preevent-footer {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 1rem;
        align-items: center;
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid rgba(245, 230, 211, 0.12);
      }
      .atw-preevent-capacity-text {
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
        margin-bottom: 0.5rem;
      }
      .atw-preevent-capacity-bar {
        height: 4px;
        background: rgba(245, 230, 211, 0.1);
        border-radius: 2px;
        overflow: hidden;
      }
      .atw-preevent-capacity-fill {
        height: 100%;
        background: linear-gradient(90deg, var(--atw-pink), var(--atw-gold));
        border-radius: 2px;
        transition: width 1s ease;
      }
      .atw-preevent-rsvp {
        display: inline-flex; align-items: center; gap: 0.5rem;
        padding: 0.625rem 1rem;
        background: var(--atw-cream);
        color: var(--atw-ink);
        border-radius: 999px;
        font-size: 0.8125rem;
        font-weight: 600;
        transition: all 0.25s ease;
      }
      .atw-preevent-rsvp:hover {
        background: var(--atw-pink);
        color: var(--atw-cream);
        transform: translateX(4px);
      }

      @media (max-width: 760px) { .atw-preevent-grid { grid-template-columns: 1fr; } }

      /* Venue map */
      .atw-preevent-map-strip {
        padding: 2.5rem;
        background: linear-gradient(135deg, rgba(29, 17, 29, 0.6), rgba(13, 4, 9, 0.8));
        border-radius: 24px;
        border: 1px solid rgba(245, 230, 211, 0.1);
      }
      .atw-preevent-map-header { margin-bottom: 1.5rem; }
      .atw-preevent-map-title {
        font-family: 'Fraunces', serif;
        font-size: 1.5rem;
        font-weight: 400;
        letter-spacing: -0.02em;
        color: var(--atw-cream);
        margin: 0;
      }
      .atw-venue-map {
        background: radial-gradient(ellipse at center, rgba(29, 17, 29, 0.8), rgba(13, 4, 9, 0.95));
        border-radius: 16px;
        overflow: hidden;
      }
      .atw-venue-map-svg { width: 100%; height: auto; display: block; }
      .atw-venue-pulse {
        animation: atw-venuePulse 3s ease-in-out infinite;
        transform-origin: center;
      }
      @keyframes atw-venuePulse {
        0%, 100% { r: 1.2; opacity: 1; }
        50% { r: 2; opacity: 0.6; }
      }

      /* ═══════════ FAQ ═══════════ */
      .atw-faq-layout {
        display: grid;
        grid-template-columns: 0.8fr 1.4fr;
        gap: 4rem;
        align-items: start;
      }
      .atw-faq-intro {
        position: sticky;
        top: 6rem;
      }
      .atw-faq-intro-desc {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
        line-height: 1.6;
        margin: 1.25rem 0 1.5rem;
        max-width: 22rem;
      }
      .atw-faq-contact-card {
        padding: 1.5rem;
        background: linear-gradient(135deg, rgba(190, 18, 60, 0.12), rgba(66, 10, 20, 0.3));
        border: 1px solid rgba(244, 114, 182, 0.2);
        border-radius: 16px;
      }
      .atw-faq-contact-label {
        font-size: 0.625rem;
        letter-spacing: 0.15em;
        font-weight: 600;
        color: var(--atw-pink);
        margin-bottom: 0.5rem;
      }
      .atw-faq-contact-email {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.9375rem;
        color: var(--atw-cream);
        margin-bottom: 1rem;
      }
      .atw-faq-contact-team {
        display: flex; align-items: center; gap: 0.75rem;
        padding-top: 1rem;
        border-top: 1px solid rgba(245, 230, 211, 0.1);
      }
      .atw-faq-contact-avatar {
        width: 28px; height: 28px;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 0.625rem;
        font-weight: 600;
        color: var(--atw-cream);
        margin-right: -8px;
        border: 2px solid var(--atw-burgundy);
      }
      .atw-faq-contact-team-text {
        margin-left: 1rem;
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.7;
      }

      .atw-faq-list { display: flex; flex-direction: column; gap: 0; }
      .atw-faq-item {
        border-bottom: 1px solid rgba(245, 230, 211, 0.08);
        transition: background 0.3s ease;
        cursor: pointer;
      }
      .atw-faq-item:hover { background: rgba(245, 230, 211, 0.02); }
      .atw-faq-question {
        width: 100%;
        padding: 1.5rem 0;
        display: grid;
        grid-template-columns: 40px 1fr 32px;
        gap: 1rem;
        align-items: center;
        text-align: left;
        transition: all 0.3s ease;
      }
      .atw-faq-q-num {
        font-family: 'JetBrains Mono', monospace;
        font-size: 0.75rem;
        color: var(--atw-cream-warm);
        opacity: 0.4;
        font-weight: 600;
      }
      .atw-faq-q-text {
        font-family: 'Fraunces', serif;
        font-size: 1.25rem;
        font-weight: 400;
        letter-spacing: -0.01em;
        color: var(--atw-cream);
        line-height: 1.3;
      }
      .atw-faq-item.is-open .atw-faq-q-num { color: var(--atw-pink); opacity: 1; }
      .atw-faq-item.is-open .atw-faq-q-text { color: var(--atw-pink); }

      .atw-faq-toggle {
        width: 32px; height: 32px;
        border-radius: 50%;
        background: rgba(245, 230, 211, 0.06);
        border: 1px solid rgba(245, 230, 211, 0.1);
        position: relative;
        transition: all 0.4s cubic-bezier(0.2, 0.9, 0.3, 1);
      }
      .atw-faq-toggle::before, .atw-faq-toggle::after {
        content: '';
        position: absolute;
        top: 50%; left: 50%;
        background: var(--atw-cream);
        border-radius: 1px;
      }
      .atw-faq-toggle::before {
        width: 12px; height: 1.5px;
        transform: translate(-50%, -50%);
      }
      .atw-faq-toggle::after {
        width: 1.5px; height: 12px;
        transform: translate(-50%, -50%);
        transition: transform 0.4s ease;
      }
      .atw-faq-item.is-open .atw-faq-toggle {
        background: var(--atw-pink);
        border-color: var(--atw-pink);
        transform: rotate(180deg);
      }
      .atw-faq-item.is-open .atw-faq-toggle::after { transform: translate(-50%, -50%) scaleY(0); }

      .atw-faq-answer {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.5s cubic-bezier(0.2, 0.9, 0.3, 1);
      }
      .atw-faq-item.is-open .atw-faq-answer { grid-template-rows: 1fr; }
      .atw-faq-answer-inner {
        overflow: hidden;
        padding: 0 0 0 56px;
      }
      .atw-faq-item.is-open .atw-faq-answer-inner {
        padding: 0 0 1.5rem 56px;
      }
      .atw-faq-answer-inner {
        font-size: 0.9375rem;
        color: var(--atw-cream-warm);
        opacity: 0.8;
        line-height: 1.7;
        max-width: 38rem;
      }

      @media (max-width: 900px) {
        .atw-faq-layout { grid-template-columns: 1fr; gap: 2rem; }
        .atw-faq-intro { position: static; }
        .atw-faq-question { grid-template-columns: 30px 1fr 28px; }
        .atw-faq-answer-inner, .atw-faq-item.is-open .atw-faq-answer-inner { padding-left: 46px; }
      }

      /* ═══════════ FINAL CTA ═══════════ */
      .atw-final-cta {
        padding: 6rem 2.5rem;
        position: relative;
      }
      .atw-final-cta-inner {
        max-width: 42rem;
        margin: 0 auto;
        text-align: center;
        position: relative;
        padding: 4rem 3rem;
        background:
          linear-gradient(135deg, rgba(190, 18, 60, 0.2), rgba(66, 10, 20, 0.4)),
          radial-gradient(ellipse at top, rgba(244, 114, 182, 0.15), transparent 70%);
        border: 1px solid rgba(244, 114, 182, 0.2);
        border-radius: 32px;
        overflow: hidden;
      }
      .atw-final-cta-inner::before {
        content: '';
        position: absolute;
        top: -50%; right: -20%;
        width: 400px; height: 400px;
        background: radial-gradient(circle, rgba(252, 211, 77, 0.2), transparent 70%);
        filter: blur(40px);
      }
      .atw-final-cta-inner > * { position: relative; z-index: 1; }
      .atw-final-cta-eyebrow {
        display: inline-flex; align-items: center; gap: 0.5rem;
        font-size: 0.6875rem;
        letter-spacing: 0.15em;
        font-weight: 600;
        color: var(--atw-gold-soft);
        padding: 0.5rem 1rem;
        border: 1px solid rgba(252, 211, 77, 0.3);
        border-radius: 999px;
        margin-bottom: 2rem;
      }
      .atw-final-cta-dot {
        width: 6px; height: 6px;
        background: var(--atw-gold-soft);
        border-radius: 50%;
        animation: atw-pulse 1.5s infinite;
        box-shadow: 0 0 10px var(--atw-gold-soft);
      }
      .atw-final-cta-title {
        font-family: 'Fraunces', serif;
        font-size: clamp(2.5rem, 6vw, 4.5rem);
        font-weight: 300;
        letter-spacing: -0.035em;
        line-height: 1;
        color: var(--atw-cream);
        margin: 0 0 1.5rem;
      }
      .atw-final-cta-title em {
        font-style: italic;
        font-weight: 400;
        background: linear-gradient(105deg, var(--atw-pink), var(--atw-gold));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      .atw-final-cta-sub {
        font-size: 1.0625rem;
        color: var(--atw-cream-warm);
        opacity: 0.85;
        line-height: 1.6;
        margin-bottom: 2.5rem;
      }
      .atw-final-cta-actions {
        display: flex;
        gap: 1rem;
        justify-content: center;
        flex-wrap: wrap;
      }

      /* Buttons */
      .atw-btn {
        display: inline-flex; align-items: center; gap: 0.625rem;
        padding: 0.875rem 1.5rem;
        border-radius: 999px;
        font-size: 0.9375rem;
        font-weight: 500;
        transition: all 0.25s ease;
      }
      .atw-btn-primary {
        background: var(--atw-cream);
        color: var(--atw-ink);
      }
      .atw-btn-primary:hover {
        background: var(--atw-pink);
        color: var(--atw-cream);
        transform: translateY(-2px);
        box-shadow: 0 15px 40px -12px rgba(244, 114, 182, 0.6);
      }
      .atw-btn-ghost {
        border: 1px solid rgba(245, 230, 211, 0.3);
        color: var(--atw-cream);
      }
      .atw-btn-ghost:hover {
        background: rgba(245, 230, 211, 0.1);
        border-color: var(--atw-cream);
      }
      .atw-btn-light {
        background: var(--atw-cream);
        color: var(--atw-ink);
      }
      .atw-btn-light:hover {
        background: var(--atw-pink);
        color: var(--atw-cream);
      }
      .atw-btn-large {
        padding: 1.125rem 2rem;
        font-size: 1rem;
      }
        .atw-orbit-stage {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 48px;
    align-items: center;
    margin-top: 48px;
}

@media (max-width: 900px) {
    .atw-orbit-stage {
        grid-template-columns: 1fr;
    }
}

.atw-orbit-container {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    max-width: 520px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.atw-orbit-ring {
    position: absolute;
    border: 1px dashed rgba(255, 255, 255, 0.08);
    border-radius: 50%;
    transition: border-color 0.4s ease, box-shadow 0.4s ease;
    pointer-events: auto;   /* was: none */
    cursor: pointer;
}

.atw-orbit-group {
    position: absolute;
    border-radius: 50%;
    animation: atw-orbit-spin linear infinite;
    transition: opacity 0.4s ease;
    transform-origin: center center;
    pointer-events: none;   /* NEW — let hovers fall through the big square */
}

.atw-orbit-node {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    animation: atw-orbit-counter linear infinite;
    pointer-events: auto;   /* NEW — nodes themselves stay hoverable */
    cursor: pointer;
}

.atw-orbit-sun {
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #fef3c7 0%, #f59e0b 40%, #BE123C 100%);
    box-shadow:
        0 0 60px rgba(245, 158, 11, 0.5),
        0 0 120px rgba(190, 18, 60, 0.3),
        inset -10px -10px 30px rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    animation: atw-sun-pulse 4s ease-in-out infinite;
}

@keyframes atw-sun-pulse {
    0%, 100% { box-shadow: 0 0 60px rgba(245, 158, 11, 0.5), 0 0 120px rgba(190, 18, 60, 0.3), inset -10px -10px 30px rgba(0,0,0,0.2); }
    50%      { box-shadow: 0 0 80px rgba(245, 158, 11, 0.7), 0 0 160px rgba(190, 18, 60, 0.4), inset -10px -10px 30px rgba(0,0,0,0.2); }
}

.atw-orbit-sun-core {
    text-align: center;
    color: #fff;
    font-family: inherit;
    line-height: 1.1;
    text-shadow: 0 1px 4px rgba(0,0,0,0.3);
}

.atw-orbit-sun-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    opacity: 0.9;
}

.atw-orbit-sun-label-big {
    font-size: 15px;
    font-weight: 600;
    font-style: italic;
    margin-top: 2px;
}


@keyframes atw-orbit-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}

.atw-orbit-node-wrap {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
}



@keyframes atw-orbit-counter {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to   { transform: translate(-50%, -50%) rotate(-360deg); }
}

.atw-orbit-legend {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.atw-orbit-legend-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.25s ease, transform 0.25s ease;
}

.atw-orbit-legend-item:hover,
.atw-orbit-legend-item.is-active {
    background: rgba(255, 255, 255, 0.04);
    transform: translateX(2px);
}

.atw-orbit-legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 5px;
    flex-shrink: 0;
}

.atw-orbit-legend-label {
    font-weight: 600;
    font-size: 14px;
    color: #fff;
}

.atw-orbit-legend-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.55);
    margin-top: 2px;
}
    `}</style>
    );
}